import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Spin, Alert, Button, Modal, Form, Input, Select, Row, Col, TimePicker, InputNumber, Upload, message, Table, Tooltip, Tabs } from 'antd';
import { FilterOutlined, SettingOutlined, DownOutlined, UpOutlined, DownloadOutlined, UploadOutlined, TableOutlined, UnorderedListOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Participant } from '../types/api';
import { useBiathlonEvent } from '../contexts/BiathlonEventContext';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { findAttribute } from './Skyteplott';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

const { Text, Title } = Typography;
const { Option } = Select;

interface AthleteInfo {
  uid: number;
  name: string;
}

interface Innskytter {
  id: string;
  name: string;
  phone: string;
  email: string;
  athletes?: AthleteInfo[];
}

interface InnskytingslisteSetupData {
  startInnskyting: string | null;
  sisteInnskyting: string | null;
  forsteSkive: number;
  sisteSkive: number;
  skiveData: {[key: number]: {innskytter: string, innskytter2: string, medhjelper: string}};
  tidPerPar: string;
  tableAssignments: {[key: string]: Participant | null};
  blockedCells: {[key: string]: string};
  showSkiveDetails: boolean;
  utdelingStartnummerAmmo: string;
  innpiskerTilInnskyting: string;
  pappBytteFra: string | null;
  pappBytteTil: string | null;
  antallAndreUttovere: number;
  antallInnskyttere: number;
  puljename: string;
}

interface InnskyttereListProps {
  onAssignToLane: (innskytterName: string) => void;
  filteredParticipants: Participant[];
}

const InnskyttereList: React.FC<InnskyttereListProps> = ({ onAssignToLane, filteredParticipants }) => {
  const { user } = useAuth();
  const { athletes } = useBiathlonEvent();
  const [innskyttere, setInnskyttere] = useState<Innskytter[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadInnskyttere = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Load selected profile
        const selectedProfileDoc = await getDoc(doc(db, 'userSettings', user.uid));
        if (!selectedProfileDoc.exists()) {
          setInnskyttere([]);
          setLoading(false);
          return;
        }
        
        const profileId = selectedProfileDoc.data().selectedProfileId;
        if (!profileId) {
          setInnskyttere([]);
          setLoading(false);
          return;
        }
        
        // Load innskyttere from profile
        const profileDoc = await getDoc(doc(db, 'profiles', profileId));
        if (!profileDoc.exists()) {
          setInnskyttere([]);
          setLoading(false);
          return;
        }
        
        const profileData = profileDoc.data();
        let data = profileData.innskyttere || [];
        
        // Migrate old format if needed
        data = data.map((innskytter: any) => {
          let athletesData = innskytter.athletes || [];
          if (athletesData.length > 0 && typeof athletesData[0] === 'number') {
            athletesData = athletesData.map((uid: number) => {
              const athlete = athletes.find(a => a.Utover.UID === uid);
              return {
                uid,
                name: athlete?.Utover.NavnFormatert || 'Unknown Athlete'
              };
            });
          }
          return {
            ...innskytter,
            athletes: athletesData
          };
        });
        
        // Filter to only include innskyttere whose athletes are in the filtered participants list
        // OR innskyttere without any assigned athletes (always visible)
        const filteredAthleteUIDs = filteredParticipants.map(p => p.Utover.UID);
        const filtered = data.filter((innskytter: Innskytter) => {
          if (!innskytter.athletes || innskytter.athletes.length === 0) return true;
          return innskytter.athletes.some((athleteInfo: AthleteInfo) => filteredAthleteUIDs.includes(athleteInfo.uid));
        });
        
        setInnskyttere(filtered);
      } catch (error) {
        console.error('Error loading innskyttere:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInnskyttere();
  }, [user, athletes, filteredParticipants]);

  if (loading) {
    return <Spin size="small" />;
  }

  if (innskyttere.length === 0) {
    return (
      <div style={{ marginTop: '8px' }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Ingen deltakende innskyttere
        </Text>
      </div>
    );
  }

  const handleInnskytterClick = (innskytter: Innskytter) => {
    onAssignToLane(innskytter.name);
  };

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ fontWeight: '500', fontSize: '12px', marginBottom: '4px' }}>Innskyttere:</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {innskyttere.map(innskytter => (
          <div
            key={innskytter.id}
            onClick={() => handleInnskytterClick(innskytter)}
            style={{
              padding: '3px 6px',
              backgroundColor: '#fff',
              border: '1px solid #e8e8e8',
              borderRadius: '3px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
          >
            {innskytter.name}
          </div>
        ))}
      </div>
    </div>
  );
};

const Innskytingsliste: React.FC = () => {
  const navigate = useNavigate();
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  
  const [startInnskyting, setStartInnskyting] = useState<dayjs.Dayjs | null>(null);
  const [sisteInnskyting, setSisteInnskyting] = useState<dayjs.Dayjs | null>(null);
  const [forsteSkive, setForsteSkive] = useState<number>(1);
  const [sisteSkive, setSisteSkive] = useState<number>(5);
  const [skiveData, setSkiveData] = useState<{[key: number]: {innskytter: string, innskytter2: string, medhjelper: string}}>({});
  const [showSkiveDetails, setShowSkiveDetails] = useState<boolean>(false);
  const [tidPerPar, setTidPerPar] = useState<string>('10');
  const [tableAssignments, setTableAssignments] = useState<{[key: string]: Participant | null}>({});
  const [blockedCells, setBlockedCells] = useState<{[key: string]: string}>({});
  const [draggedParticipant, setDraggedParticipant] = useState<Participant | null>(null);
  const [blockCellModalVisible, setBlockCellModalVisible] = useState(false);
  const [selectedCellForBlock, setSelectedCellForBlock] = useState<string | null>(null);
  const [blockCellReason, setBlockCellReason] = useState<string>('');
  const [utdelingStartnummerAmmo, setUtdelingStartnummerAmmo] = useState<string>('');
  const [innpiskerTilInnskyting, setInnpiskerTilInnskyting] = useState<string>('');
  const [pappBytteFra, setPappBytteFra] = useState<dayjs.Dayjs | null>(null);
  const [pappBytteTil, setPappBytteTil] = useState<dayjs.Dayjs | null>(null);
  const [antallAndreUttovere, setAntallAndreUttovere] = useState<number>(0);
  const [antallInnskyttere, setAntallInnskyttere] = useState<number>(1);
  const [puljename, setPuljename] = useState<string>('');
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);
  const [isLoadingGroup, setIsLoadingGroup] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [groupToDelete, setGroupToDelete] = useState<string>('');
  const isRenamingRef = useRef<boolean>(false);

  const [tempStartInnskyting, setTempStartInnskyting] = useState<dayjs.Dayjs | null>(null);
  const [tempSisteInnskyting, setTempSisteInnskyting] = useState<dayjs.Dayjs | null>(null);
  const [tempForsteSkive, setTempForsteSkive] = useState<number>(1);
  const [tempSisteSkive, setTempSisteSkive] = useState<number>(5);
  const [tempSkiveData, setTempSkiveData] = useState<{[key: number]: {innskytter: string, innskytter2: string, medhjelper: string}}>({});
  const [tempTidPerPar, setTempTidPerPar] = useState<string>('10');
  const [tempShowSkiveDetails, setTempShowSkiveDetails] = useState<boolean>(false);
  const [tempShowAdvanced, setTempShowAdvanced] = useState<boolean>(false);
  const [tempUtdelingStartnummerAmmo, setTempUtdelingStartnummerAmmo] = useState<string>('');
  const [tempInnpiskerTilInnskyting, setTempInnpiskerTilInnskyting] = useState<string>('');
  const [tempPappBytteFra, setTempPappBytteFra] = useState<dayjs.Dayjs | null>(null);
  const [tempPappBytteTil, setTempPappBytteTil] = useState<dayjs.Dayjs | null>(null);
  const [tempAntallAndreUttovere, setTempAntallAndreUttovere] = useState<number>(0);
  const [tempAntallInnskyttere, setTempAntallInnskyttere] = useState<number>(1);
  const [tempPuljename, setTempPuljename] = useState<string>('');

  const [innskyttereData, setInnskyttereData] = useState<Innskytter[]>([]);

  const { user } = useAuth();
  const { selectedEvent, athletes, loading, error, clubs, teams, kretser, classes, selectedClubs, setSelectedClubs, selectedTeams, setSelectedTeams, selectedKretser, setSelectedKretser, selectedClasses, setSelectedClasses, nameFilter, setNameFilter, excludeBibs, setExcludeBibs, startTimeFrom, setStartTimeFrom, startTimeTo, setStartTimeTo } = useBiathlonEvent();
  const participants = athletes;

  console.log('Innskytingsliste - selectedEvent:', selectedEvent);
  console.log('Innskytingsliste - loading:', loading);
  console.log('Innskytingsliste - error:', error);

  const calculateDefaultTimePerPair = useCallback(() => {
    if (!startInnskyting || !sisteInnskyting || participants.length === 0) {
      return '';
    }
    
    try {
      const totalMinutes = sisteInnskyting.diff(startInnskyting, 'minute');
      
      if (totalMinutes <= 0) {
        return '';
      }
      
      const numberOfSkiver = sisteSkive - forsteSkive + 1;
      const totalTimeAcrossLanes = totalMinutes * numberOfSkiver;
      const timePerPair = totalTimeAcrossLanes / (participants.length / 2);
      return `${timePerPair.toFixed(1)} min`;
    } catch (error) {
      return '';
    }
  }, [startInnskyting, sisteInnskyting, forsteSkive, sisteSkive, participants.length]);

  useEffect(() => {
    if (!tidPerPar) {
      const defaultValue = calculateDefaultTimePerPair();
      if (defaultValue) {
        setTidPerPar(defaultValue);
      }
    }
  }, [startInnskyting, sisteInnskyting, forsteSkive, sisteSkive, participants.length, tidPerPar, calculateDefaultTimePerPair]);

  // Load innskyttere data from selected profile
  useEffect(() => {
    const loadInnskyttere = async () => {
      if (!user || !selectedProfileId) return;
      
      try {
        const profileDoc = await getDoc(doc(db, 'profiles', selectedProfileId));
        if (!profileDoc.exists()) return;
        
        const profileData = profileDoc.data();
        let data = profileData.innskyttere || [];
        
        // Migrate old format if needed
        data = data.map((innskytter: any) => {
          let athletesData = innskytter.athletes || [];
          if (athletesData.length > 0 && typeof athletesData[0] === 'number') {
            athletesData = athletesData.map((uid: number) => {
              const athlete = athletes.find(a => a.Utover.UID === uid);
              return {
                uid,
                name: athlete?.Utover.NavnFormatert || 'Unknown Athlete'
              };
            });
          }
          return {
            ...innskytter,
            athletes: athletesData
          };
        });
        
        setInnskyttereData(data);
      } catch (error) {
        console.error('Error loading innskyttere:', error);
      }
    };

    loadInnskyttere();
  }, [user, athletes, selectedProfileId]);

  // Load available groups for current event
  useEffect(() => {
    const loadAvailableGroups = async () => {
      if (!selectedEvent || !user || !selectedProfileId) {
        setAvailableGroups([]);
        return;
      }
      
      try {
        const profileDoc = await getDoc(doc(db, 'profiles', selectedProfileId));
        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          const eventSetups = profileData.setupData?.[selectedEvent.Id];
          if (eventSetups) {
            // Check if this is old format - old format will have direct properties like startInnskyting
            // New format only has group names as keys, each containing setup/filters/tableAssignments
            const isOldFormat = ('startInnskyting' in eventSetups) || ('sisteInnskyting' in eventSetups) || ('forsteSkive' in eventSetups);
            
            if (isOldFormat) {
              // This is old format - migrate it
              console.log('Migrating old data format to new nested structure');
              const migratedData = {
                'Default': {
                  setup: {
                    startInnskyting: eventSetups.startInnskyting,
                    sisteInnskyting: eventSetups.sisteInnskyting,
                    forsteSkive: eventSetups.forsteSkive,
                    sisteSkive: eventSetups.sisteSkive,
                    skiveData: eventSetups.skiveData,
                    tidPerPar: eventSetups.tidPerPar,
                    showSkiveDetails: eventSetups.showSkiveDetails,
                    utdelingStartnummerAmmo: eventSetups.utdelingStartnummerAmmo,
                    innpiskerTilInnskyting: eventSetups.innpiskerTilInnskyting,
                    pappBytteFra: eventSetups.pappBytteFra,
                    pappBytteTil: eventSetups.pappBytteTil,
                    puljename: 'Default',
                    blockedCells: eventSetups.blockedCells,
                    antallAndreUttovere: eventSetups.antallAndreUttovere,
                    antallInnskyttere: eventSetups.antallInnskyttere
                  },
                  filters: {},
                  tableAssignments: eventSetups.tableAssignments || {}
                }
              };
              
              // Save migrated data - REPLACE the old structure completely
              const profileRef = doc(db, 'profiles', selectedProfileId);
              const updatedSetupData = {
                ...profileData.setupData,
                [selectedEvent.Id]: migratedData  // This completely replaces the old structure
              };
              
              await setDoc(profileRef, {
                ...profileData,
                setupData: updatedSetupData,
                updatedAt: new Date().toISOString()
              });
              
              setAvailableGroups(['Default']);
              if (!puljename) {
                setPuljename('Default');
              }
            } else {
              // New format - just get the group names and sort alphabetically
              const groups = Object.keys(eventSetups).sort();
              setAvailableGroups(groups);
              // If no puljename is set, select the first available group
              if (!puljename && groups.length > 0) {
                setPuljename(groups[0]);
              }
            }
          } else {
            // No groups exist - create "Pulje 1"
            console.log('No groups found, creating Pulje 1');
            const profileRef = doc(db, 'profiles', selectedProfileId);
            const newPuljeData = {
              'Pulje 1': {
                setup: {
                  startInnskyting: null,
                  sisteInnskyting: null,
                  forsteSkive: 1,
                  sisteSkive: 5,
                  skiveData: {},
                  tidPerPar: '10',
                  showSkiveDetails: false,
                  utdelingStartnummerAmmo: '',
                  innpiskerTilInnskyting: '',
                  pappBytteFra: null,
                  pappBytteTil: null,
                  puljename: 'Pulje 1',
                  blockedCells: {},
                  antallAndreUttovere: 0,
                  antallInnskyttere: 1
                },
                filters: {},
                tableAssignments: {}
              }
            };
            
            const updatedSetupData = {
              ...profileData.setupData,
              [selectedEvent.Id]: newPuljeData
            };
            
            await setDoc(profileRef, {
              ...profileData,
              setupData: updatedSetupData,
              updatedAt: new Date().toISOString()
            });
            
            setAvailableGroups(['Pulje 1']);
            setPuljename('Pulje 1');
          }
        }
      } catch (error) {
        console.error('Error loading available groups:', error);
        setAvailableGroups([]);
      }
    };

    loadAvailableGroups();
  }, [selectedEvent, user, selectedProfileId, puljename]);

  const getStoredSetup = useCallback(async (): Promise<InnskytingslisteSetupData | null> => {
    console.log('getStoredSetup called with selectedEvent:', selectedEvent, 'selectedProfileId:', selectedProfileId, 'puljename:', puljename);
    if (!selectedEvent || !user || !selectedProfileId) {
      console.log('No selectedEvent, user, or selectedProfileId');
      return null;
    }
    try {
      const profileDoc = await getDoc(doc(db, 'profiles', selectedProfileId));
      
      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        const eventSetups = profileData.setupData?.[selectedEvent.Id];
        if (eventSetups) {
          let puljeData = null;
          
          // If puljename is specified, get that specific setup
          if (puljename && eventSetups[puljename]) {
            puljeData = eventSetups[puljename];
            console.log('Found stored data from profile for pulje:', puljename);
          } else {
            // Otherwise, try to get the first available setup
            const setupNames = Object.keys(eventSetups);
            if (setupNames.length > 0) {
              puljeData = eventSetups[setupNames[0]];
              console.log('Found stored data from profile (first available):', setupNames[0]);
            }
          }
          
          if (puljeData) {
            // Apply filters if they exist, otherwise reset to empty
            if (puljeData.filters) {
              console.log('Applying saved filters:', puljeData.filters);
              setSelectedClubs(puljeData.filters.clubs || []);
              setSelectedTeams(puljeData.filters.teams || []);
              setSelectedKretser(puljeData.filters.kretser || []);
              setSelectedClasses(puljeData.filters.classes || []);
              setNameFilter(puljeData.filters.nameSearch || '');
              setExcludeBibs(puljeData.filters.excludeBibs || '');
              
              // Handle both old format (startTime) and new format (startTimeFrom/startTimeTo)
              if (puljeData.filters.startTimeFrom !== undefined || puljeData.filters.startTimeTo !== undefined) {
                // New format
                setStartTimeFrom(puljeData.filters.startTimeFrom || '');
                setStartTimeTo(puljeData.filters.startTimeTo || '');
              } else if (puljeData.filters.startTime) {
                // Old format - backward compatibility
                const [from, to] = puljeData.filters.startTime.split('-');
                setStartTimeFrom(from?.trim() || '');
                setStartTimeTo(to?.trim() || '');
              } else {
                setStartTimeFrom('');
                setStartTimeTo('');
              }
            } else {
              // No filters saved, reset all to empty
              console.log('No saved filters, resetting to empty');
              setSelectedClubs([]);
              setSelectedTeams([]);
              setSelectedKretser([]);
              setSelectedClasses([]);
              setNameFilter('');
              setExcludeBibs('');
              setStartTimeFrom('');
              setStartTimeTo('');
            }
            
            // Return the setup with tableAssignments
            return {
              ...puljeData.setup,
              tableAssignments: puljeData.tableAssignments || {}
            } as InnskytingslisteSetupData;
          }
        }
      }
    } catch (error) {
      console.error('Error reading setup from profile:', error);
    }
    return null;
  }, [selectedEvent, user, selectedProfileId, puljename]);

  // Load selected profile ID on mount
  useEffect(() => {
    const loadSelectedProfile = async () => {
      if (!user) return;
      
      try {
        const selectedProfileDoc = await getDoc(doc(db, 'userSettings', user.uid));
        if (selectedProfileDoc.exists()) {
          const newProfileId = selectedProfileDoc.data().selectedProfileId || null;
          setSelectedProfileId(newProfileId);
        }
      } catch (error) {
        console.error('Error loading selected profile:', error);
      }
    };

    if (user) {
      loadSelectedProfile();
    }
  }, [user]);

  const serializeTableAssignments = useCallback((assignments: {[key: string]: Participant | null}): {[key: string]: number | null} => {
    const serialized: {[key: string]: number | null} = {};
    Object.keys(assignments).forEach(key => {
      const participant = assignments[key];
      serialized[key] = participant ? participant.UID : null;
    });
    return serialized;
  }, []);

  const deserializeTableAssignments = useCallback((
    serialized: {[key: string]: number | null} | undefined,
    currentParticipants: Participant[]
  ): {[key: string]: Participant | null} => {
    if (!serialized) return {};
    
    const deserialized: {[key: string]: Participant | null} = {};
    Object.keys(serialized).forEach(key => {
      const uid = serialized[key];
      if (uid) {
        const participant = currentParticipants.find(p => p.UID === uid);
        if (participant) {
          deserialized[key] = participant;
        }
      } else {
        deserialized[key] = null;
      }
    });
    return deserialized;
  }, []);

  const saveSetup = useCallback(async (setupData: InnskytingslisteSetupData) => {
    if (!selectedEvent || !user || !selectedProfileId) return;
    try {
      // Get the current profile data
      const profileRef = doc(db, 'profiles', selectedProfileId);
      const profileDoc = await getDoc(profileRef);
      
      if (!profileDoc.exists()) {
        console.error('Profile not found');
        return;
      }

      const profileData = profileDoc.data();
      
      // Use puljename from setupData or 'Default'
      const setupPuljename = setupData.puljename || puljename || 'Default';
      
      // Get current event setups
      const eventSetups = profileData.setupData?.[selectedEvent.Id] || {};
      
      // Collect current filters
      const currentFilters = {
        clubs: selectedClubs,
        teams: selectedTeams,
        kretser: selectedKretser,
        classes: selectedClasses,
        nameSearch: nameFilter,
        excludeBibs: excludeBibs,
        startTimeFrom: startTimeFrom || null,
        startTimeTo: startTimeTo || null
      };
      
      // Create pulje data with setup, filters, and assignments
      const puljeData = {
        setup: {
          startInnskyting: setupData.startInnskyting,
          sisteInnskyting: setupData.sisteInnskyting,
          forsteSkive: setupData.forsteSkive,
          sisteSkive: setupData.sisteSkive,
          skiveData: setupData.skiveData,
          tidPerPar: setupData.tidPerPar,
          showSkiveDetails: setupData.showSkiveDetails,
          utdelingStartnummerAmmo: setupData.utdelingStartnummerAmmo,
          innpiskerTilInnskyting: setupData.innpiskerTilInnskyting,
          pappBytteFra: setupData.pappBytteFra,
          pappBytteTil: setupData.pappBytteTil,
          puljename: setupPuljename,
          blockedCells: setupData.blockedCells,
          antallAndreUttovere: setupData.antallAndreUttovere,
          antallInnskyttere: setupData.antallInnskyttere
        },
        filters: currentFilters,
        tableAssignments: serializeTableAssignments(setupData.tableAssignments)
      };

      // Update the profile with the new pulje data
      const updatedSetupData = {
        ...profileData.setupData,
        [selectedEvent.Id]: {
          ...eventSetups,
          [setupPuljename]: puljeData
        }
      };

      await setDoc(profileRef, {
        ...profileData,
        setupData: updatedSetupData,
        updatedAt: new Date().toISOString()
      });
      
      console.log('Setup saved to profile for event:', selectedEvent.Id, 'pulje:', setupPuljename, 'with', Object.keys(setupData.tableAssignments).filter(k => setupData.tableAssignments[k]).length, 'assignments');
    } catch (error) {
      console.error('Error saving setup to profile:', error);
      message.error('Failed to save setup');
    }
  }, [selectedEvent, user, selectedProfileId, puljename, serializeTableAssignments, selectedClubs, selectedTeams, selectedKretser, selectedClasses, nameFilter, excludeBibs, startTimeFrom, startTimeTo]);

  const autoSaveSetup = useCallback(() => {
    const setupData: InnskytingslisteSetupData = {
      startInnskyting: startInnskyting?.format('HH:mm') || null,
      sisteInnskyting: sisteInnskyting?.format('HH:mm') || null,
      forsteSkive,
      sisteSkive,
      skiveData,
      tidPerPar,
      tableAssignments,
      blockedCells,
      showSkiveDetails,
      utdelingStartnummerAmmo,
      innpiskerTilInnskyting,
      pappBytteFra: pappBytteFra?.format('HH:mm') || null,
      pappBytteTil: pappBytteTil?.format('HH:mm') || null,
      antallAndreUttovere,
      antallInnskyttere,
      puljename
    };
    saveSetup(setupData);
  }, [startInnskyting, sisteInnskyting, forsteSkive, sisteSkive, skiveData, tidPerPar, tableAssignments, blockedCells, showSkiveDetails, utdelingStartnummerAmmo, innpiskerTilInnskyting, pappBytteFra, pappBytteTil, antallAndreUttovere, antallInnskyttere, saveSetup]);

  useEffect(() => {
    if (!selectedEvent) {
      navigate('/');
    }
  }, [selectedEvent, navigate]);

  console.log('=== ABOUT TO DEFINE LOADING USEEFFECT ===');
  useEffect(() => {
    const loadSetup = async () => {
      console.log('=== LOADING SETUP USEEFFECT TRIGGERED ===');
      console.log('Loading setup from Firestore - selectedEvent:', selectedEvent);
      
      if (!selectedEvent || !user) {
        console.log('No selectedEvent or user available, skipping setup loading');
        return;
      }
      
      if (participants.length === 0) {
        console.log('Participants not yet loaded, skipping setup loading');
        return;
      }
      
      setIsLoadingGroup(true);
      
      try {
        const storedSetup = await getStoredSetup();
        
        if (storedSetup) {
          console.log('Found stored setup:', storedSetup);
          console.log('- startInnskyting from storage:', storedSetup.startInnskyting);
          console.log('- sisteInnskyting from storage:', storedSetup.sisteInnskyting);
          console.log('- skiveData from storage:', storedSetup.skiveData);
          console.log('- showSkiveDetails from storage:', storedSetup.showSkiveDetails);
          
          setStartInnskyting(storedSetup.startInnskyting ? dayjs(storedSetup.startInnskyting, 'HH:mm') : null);
          setSisteInnskyting(storedSetup.sisteInnskyting ? dayjs(storedSetup.sisteInnskyting, 'HH:mm') : null);
          setForsteSkive(storedSetup.forsteSkive);
          setSisteSkive(storedSetup.sisteSkive);
          setSkiveData(storedSetup.skiveData || {});
          setTidPerPar(storedSetup.tidPerPar || '');
          
          const serializedAssignments = storedSetup.tableAssignments as any as {[key: string]: number | null};
          console.log('Raw stored tableAssignments keys count:', Object.keys(serializedAssignments).filter(k => serializedAssignments[k]).length);
          console.log('First 10 stored assignment keys:', Object.keys(serializedAssignments).filter(k => serializedAssignments[k]).slice(0, 10));
          console.log('Participants available for matching:', participants.length);
          const rehydratedAssignments = deserializeTableAssignments(serializedAssignments, participants);
          console.log('Rehydrated', Object.keys(rehydratedAssignments).filter(k => rehydratedAssignments[k]).length, 'assignments from Firestore');
          console.log('First 10 rehydrated keys:', Object.keys(rehydratedAssignments).filter(k => rehydratedAssignments[k]).slice(0, 10));
          setTableAssignments(rehydratedAssignments);
          
          setShowSkiveDetails(storedSetup.showSkiveDetails || false);
          setBlockedCells(storedSetup.blockedCells || {});
          setUtdelingStartnummerAmmo(storedSetup.utdelingStartnummerAmmo || '');
          setInnpiskerTilInnskyting(storedSetup.innpiskerTilInnskyting || '');
          setPappBytteFra(storedSetup.pappBytteFra ? dayjs(storedSetup.pappBytteFra, 'HH:mm') : null);
          setPappBytteTil(storedSetup.pappBytteTil ? dayjs(storedSetup.pappBytteTil, 'HH:mm') : null);
          setAntallAndreUttovere(storedSetup.antallAndreUttovere || 0);
          setAntallInnskyttere(storedSetup.antallInnskyttere || 1);
          setPuljename(storedSetup.puljename || '');
          console.log('=== SETUP LOADING COMPLETED ===');
          setIsLoadingGroup(false);
        } else {
          console.log('No stored setup found for event', selectedEvent.Id);
          setIsLoadingGroup(false);
        }
      } catch (error) {
        console.error('Error loading setup from Firestore:', error);
        setIsLoadingGroup(false);
      }
    };
    
    loadSetup();
  }, [selectedEvent, user, participants, deserializeTableAssignments, getStoredSetup, selectedProfileId, puljename]);

  useEffect(() => {
    if (isRenamingRef.current) {
      console.log('=== AUTO-SAVE BLOCKED: Rename in progress ===');
      return;
    }
    if (selectedEvent && !isLoadingGroup) {
      console.log('=== AUTO-SAVE TRIGGERED ===');
      console.log('Current values - startInnskyting:', startInnskyting?.format('HH:mm'));
      console.log('Current values - sisteInnskyting:', sisteInnskyting?.format('HH:mm'));
      console.log('Current values - tidPerPar:', tidPerPar);
      console.log('Current values - showSkiveDetails:', showSkiveDetails);
      console.log('Current tableAssignments count:', Object.keys(tableAssignments).filter(k => tableAssignments[k]).length);
      console.log('Sample tableAssignments keys:', Object.keys(tableAssignments).filter(k => tableAssignments[k]).slice(0, 5));
      autoSaveSetup();
    }
  }, [startInnskyting, sisteInnskyting, forsteSkive, sisteSkive, skiveData, tidPerPar, tableAssignments, blockedCells, showSkiveDetails, utdelingStartnummerAmmo, innpiskerTilInnskyting, pappBytteFra, pappBytteTil, antallAndreUttovere, antallInnskyttere, puljename, selectedEvent, autoSaveSetup, isLoadingGroup, selectedClubs, selectedTeams, selectedKretser, selectedClasses, nameFilter, excludeBibs, startTimeFrom, startTimeTo]);



  const handlePrint = () => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Add header information with two columns
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Innskytingsliste', pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      let yPos = 25;
      const leftX = 14;
      const rightX = pageWidth - 14;
      
      // Left column - Event information
      if (selectedEvent) {
        doc.text(`Arrangement: ${selectedEvent.Name}`, leftX, yPos);
      }
      
      // Right column - Date, Time, Lanes, First start
      if (selectedEvent) {
        doc.text(`Dato: ${selectedEvent.Date}`, rightX, yPos, { align: 'right' });
        yPos += 5;
      }
      
      if (startInnskyting || sisteInnskyting) {
        doc.text(`Tid: ${startInnskyting?.format('HH:mm') || 'N/A'} - ${sisteInnskyting?.format('HH:mm') || 'N/A'}`, rightX, yPos, { align: 'right' });
        yPos += 5;
      }
      
      if (forsteSkive && sisteSkive) {
        doc.text(`Skiver: ${forsteSkive} - ${sisteSkive}`, rightX, yPos, { align: 'right' });
        yPos += 5;
      }
      
      if (startInnskyting) {
        doc.text(`Første start: ${startInnskyting.format('HH:mm')}`, rightX, yPos, { align: 'right' });
        yPos += 5;
      }
      
      // Optional fields on the left, if present
      yPos = 30; // Reset to continue left column
      
      if (tidPerPar) {
        doc.text(`Tid pr par: ${tidPerPar}`, leftX, yPos);
        yPos += 5;
      }
      
      if (utdelingStartnummerAmmo) {
        doc.text(`Utdeling startnummer/ammo: ${utdelingStartnummerAmmo}`, leftX, yPos);
        yPos += 5;
      }
      
      if (innpiskerTilInnskyting) {
        doc.text(`Innpisker til innskyting: ${innpiskerTilInnskyting}`, leftX, yPos);
        yPos += 5;
      }
      
      yPos = Math.max(yPos, 50); // Ensure we have enough space before table
      
      // Prepare table data
      const tableData: any[] = [];
      const lanes = skiveNumbers;
      
      // Build lane header row
      const laneHeaders: any[] = [{ 
        content: 'Tid', 
        styles: { halign: 'center', fontStyle: 'bold', fillColor: [200, 200, 200], fontSize: 10 } 
      }];
      
      lanes.forEach(lane => {
        laneHeaders.push({ 
          content: `Skive ${lane}`, 
          styles: { halign: 'center', fontStyle: 'bold', fillColor: [255, 255, 0], fontSize: 10 } 
        });
      });
      tableData.push(laneHeaders);
      
      // Build innskytter info row
      const innskytterHeaders: any[] = [{ content: '', styles: { fillColor: [250, 250, 250] } }];
      lanes.forEach(lane => {
        const innskytter = skiveData[lane]?.innskytter || '';
        const innskytter2 = skiveData[lane]?.innskytter2 || '';
        let innskytterText = '';
        if (antallInnskyttere === 2) {
          innskytterText = `Innskytere:\n${innskytter}\n${innskytter2}`;
        } else {
          innskytterText = `Innskyter: ${innskytter}`;
        }
        innskytterHeaders.push({ 
          content: innskytterText, 
          styles: { fontStyle: 'bold', fontSize: 8, fillColor: [250, 250, 250] } 
        });
      });
      tableData.push(innskytterHeaders);
      
      // Build medhjelper info row
      const medhjelperHeaders: any[] = [{ content: '', styles: { fillColor: [250, 250, 250] } }];
      lanes.forEach(lane => {
        const medhjelper = skiveData[lane]?.medhjelper || '';
        medhjelperHeaders.push({ 
          content: `Medhjelper: ${medhjelper}`, 
          styles: { fontStyle: 'bold', fontSize: 8, fillColor: [250, 250, 250] } 
        });
      });
      tableData.push(medhjelperHeaders);
      
      // Build time slot rows - two rows per time slot
      timeSlots.forEach((time, index) => {
        const nextTimeSlot = index < timeSlots.length - 1 ? timeSlots[index + 1] : undefined;
        const pappbytteInfo = getPappbytteInfo(time, nextTimeSlot);
        
        // Build time cell content with pappbytte info if applicable
        let timeContent = time;
        let timeStyle: any = { halign: 'center', valign: 'middle', fontStyle: 'bold', fontSize: 10 };
        
        if (pappbytteInfo.isPappbytte && pappbytteInfo.pappbytteStart && pappbytteInfo.pappbytteEnd) {
          timeContent = `${time}\nPappbytte ${pappbytteInfo.pappbytteStart}-${pappbytteInfo.pappbytteEnd}`;
          timeStyle.fillColor = [255, 243, 205]; // Beige/yellow background for pappbytte
          timeStyle.fontSize = 9;
        }
        
        const row1: any[] = [{ 
          content: timeContent, 
          rowSpan: 2, 
          styles: timeStyle 
        }];
        const row2: any[] = [];
        
        lanes.forEach(lane => {
          const cellKey1 = `${time}-${lane}-1`;
          const cellKey2 = `${time}-${lane}-2`;
          const participant1 = tableAssignments[cellKey1];
          const participant2 = tableAssignments[cellKey2];
          const blocked1 = blockedCells[cellKey1];
          const blocked2 = blockedCells[cellKey2];
          
          // First row - participant 1 or blocked
          let text1 = '';
          let style1: any = { fontSize: 9 };
          if (blocked1) {
            text1 = blocked1;
            style1.fillColor = [200, 200, 200]; // Grey background for blocked
            style1.fontStyle = 'italic';
          } else if (participant1) {
            text1 = `${participant1.Utover.NavnFormatert} (${participant1.Startnummer})`;
          }
          
          // Second row - participant 2 or blocked
          let text2 = '';
          let style2: any = { fontSize: 9, fillColor: [245, 245, 245] }; // Light grey background for second athlete
          if (blocked2) {
            text2 = blocked2;
            style2.fillColor = [200, 200, 200]; // Grey background for blocked (darker than regular row)
            style2.fontStyle = 'italic';
          } else if (participant2) {
            text2 = `${participant2.Utover.NavnFormatert} (${participant2.Startnummer})`;
          }
          
          row1.push({ content: text1, styles: style1 });
          row2.push({ content: text2, styles: style2 });
        });
        
        tableData.push(row1);
        tableData.push(row2);
      });
      
      // Generate table with autoTable
      autoTable(doc, {
        startY: yPos,
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 10,
          cellPadding: 2,
          overflow: 'linebreak',
          lineColor: [0, 0, 0],
          lineWidth: 0.5
        },
        didDrawCell: (data: any) => {
          // Draw light grey line between specific rows
          if (data.section === 'body') {
            const { x, y, width } = data.cell;
            
            // Between innskytter and medhjelper rows (row index 1 to 2)
            if (data.row.index === 2) {
              doc.setDrawColor(180, 180, 180); // Light grey
              doc.setLineWidth(0.5);
              doc.line(x, y, x + width, y);
              doc.setDrawColor(0, 0, 0); // Reset to black
            }
            
            // Between athletes in the same time slot (starting from row 3 onwards)
            if (data.row.index >= 3) {
              const rowIndexInData = data.row.index - 3; // Adjust for header rows
              // For odd rows (second athlete), draw light grey line on top
              if (rowIndexInData % 2 === 1) {
                doc.setDrawColor(180, 180, 180); // Light grey
                doc.setLineWidth(0.5);
                doc.line(x, y, x + width, y);
                doc.setDrawColor(0, 0, 0); // Reset to black
              }
            }
          }
        },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto'
      });
      
      // Save the PDF
      const fileName = `innskytingsliste-${selectedEvent?.Name || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      message.success('PDF generated successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    }
  };

  const handleDownloadBothReports = async () => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4'); // Start with landscape for first report
      let pageWidth = doc.internal.pageSize.getWidth();
      
      // ===== FIRST REPORT: INNSKYTINGSLISTE (LANDSCAPE) =====
      // Add header information with two columns
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Innskytingsliste', pageWidth / 2, 15, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      let yPos = 25;
      const leftX = 14;
      let rightX = pageWidth - 14;
      
      // Left column - Event information
      if (selectedEvent) {
        doc.text(`Arrangement: ${selectedEvent.Name}`, leftX, yPos);
      }
      
      // Right column - Date, Time, Lanes, First start
      if (selectedEvent) {
        doc.text(`Dato: ${selectedEvent.Date}`, rightX, yPos, { align: 'right' });
        yPos += 5;
      }
      
      if (startInnskyting || sisteInnskyting) {
        doc.text(`Tid: ${startInnskyting?.format('HH:mm') || 'N/A'} - ${sisteInnskyting?.format('HH:mm') || 'N/A'}`, rightX, yPos, { align: 'right' });
        yPos += 5;
      }
      
      if (forsteSkive && sisteSkive) {
        doc.text(`Skiver: ${forsteSkive} - ${sisteSkive}`, rightX, yPos, { align: 'right' });
        yPos += 5;
      }
      
      if (startInnskyting) {
        doc.text(`Første start: ${startInnskyting.format('HH:mm')}`, rightX, yPos, { align: 'right' });
        yPos += 5;
      }
      
      // Optional fields on the left, if present
      yPos = 30; // Reset to continue left column
      
      if (tidPerPar) {
        doc.text(`Tid pr par: ${tidPerPar}`, leftX, yPos);
        yPos += 5;
      }
      
      if (utdelingStartnummerAmmo) {
        doc.text(`Utdeling startnummer/ammo: ${utdelingStartnummerAmmo}`, leftX, yPos);
        yPos += 5;
      }
      
      if (innpiskerTilInnskyting) {
        doc.text(`Innpisker til innskyting: ${innpiskerTilInnskyting}`, leftX, yPos);
        yPos += 5;
      }
      
      yPos = Math.max(yPos, 50); // Ensure we have enough space before table
      
      // Prepare table data for innskytingsliste
      const tableData1: any[] = [];
      const lanes = skiveNumbers;
      
      // Build lane header row
      const laneHeaders: any[] = [{ 
        content: 'Tid', 
        styles: { halign: 'center', fontStyle: 'bold', fillColor: [200, 200, 200], fontSize: 10 } 
      }];
      
      lanes.forEach(lane => {
        laneHeaders.push({ 
          content: `Skive ${lane}`, 
          styles: { halign: 'center', fontStyle: 'bold', fillColor: [255, 255, 0], fontSize: 10 } 
        });
      });
      tableData1.push(laneHeaders);
      
      // Build innskytter info row
      const innskytterHeaders: any[] = [{ content: '', styles: { fillColor: [250, 250, 250] } }];
      lanes.forEach(lane => {
        const innskytter = skiveData[lane]?.innskytter || '';
        const innskytter2 = skiveData[lane]?.innskytter2 || '';
        let innskytterText = '';
        if (antallInnskyttere === 2) {
          innskytterText = `Innskytere:\n${innskytter}\n${innskytter2}`;
        } else {
          innskytterText = `Innskyter: ${innskytter}`;
        }
        innskytterHeaders.push({ 
          content: innskytterText, 
          styles: { fontStyle: 'bold', fontSize: 8, fillColor: [250, 250, 250] } 
        });
      });
      tableData1.push(innskytterHeaders);
      
      // Build medhjelper info row
      const medhjelperHeaders: any[] = [{ content: '', styles: { fillColor: [250, 250, 250] } }];
      lanes.forEach(lane => {
        const medhjelper = skiveData[lane]?.medhjelper || '';
        medhjelperHeaders.push({ 
          content: `Medhjelper: ${medhjelper}`, 
          styles: { fontStyle: 'bold', fontSize: 8, fillColor: [250, 250, 250] } 
        });
      });
      tableData1.push(medhjelperHeaders);
      
      // Build time slot rows - two rows per time slot
      timeSlots.forEach((time, index) => {
        const nextTimeSlot = index < timeSlots.length - 1 ? timeSlots[index + 1] : undefined;
        const pappbytteInfo = getPappbytteInfo(time, nextTimeSlot);
        
        // Build time cell content with pappbytte info if applicable
        let timeContent = time;
        let timeStyle: any = { halign: 'center', valign: 'middle', fontStyle: 'bold', fontSize: 10 };
        
        if (pappbytteInfo.isPappbytte && pappbytteInfo.pappbytteStart && pappbytteInfo.pappbytteEnd) {
          timeContent = `${time}\nPappbytte ${pappbytteInfo.pappbytteStart}-${pappbytteInfo.pappbytteEnd}`;
          timeStyle.fillColor = [255, 243, 205]; // Beige/yellow background for pappbytte
          timeStyle.fontSize = 9;
        }
        
        const row1: any[] = [{ 
          content: timeContent, 
          rowSpan: 2, 
          styles: timeStyle 
        }];
        const row2: any[] = [];
        
        lanes.forEach(lane => {
          const cellKey1 = `${time}-${lane}-1`;
          const cellKey2 = `${time}-${lane}-2`;
          const participant1 = tableAssignments[cellKey1];
          const participant2 = tableAssignments[cellKey2];
          const blocked1 = blockedCells[cellKey1];
          const blocked2 = blockedCells[cellKey2];
          
          // First row - participant 1 or blocked
          let text1 = '';
          let style1: any = { fontSize: 9 };
          if (blocked1) {
            text1 = blocked1;
            style1.fillColor = [200, 200, 200]; // Grey background for blocked
            style1.fontStyle = 'italic';
          } else if (participant1) {
            text1 = `${participant1.Utover.NavnFormatert} (${participant1.Startnummer})`;
          }
          
          // Second row - participant 2 or blocked
          let text2 = '';
          let style2: any = { fontSize: 9, fillColor: [245, 245, 245] }; // Light grey background for second athlete
          if (blocked2) {
            text2 = blocked2;
            style2.fillColor = [200, 200, 200]; // Grey background for blocked (darker than regular row)
            style2.fontStyle = 'italic';
          } else if (participant2) {
            text2 = `${participant2.Utover.NavnFormatert} (${participant2.Startnummer})`;
          }
          
          row1.push({ content: text1, styles: style1 });
          row2.push({ content: text2, styles: style2 });
        });
        
        tableData1.push(row1);
        tableData1.push(row2);
      });
      
      // Generate first table (innskytingsliste)
      autoTable(doc, {
        startY: yPos,
        body: tableData1,
        theme: 'grid',
        styles: {
          fontSize: 10,
          cellPadding: 2,
          overflow: 'linebreak',
          lineColor: [0, 0, 0],
          lineWidth: 0.5
        },
        didDrawCell: (data: any) => {
          // Draw light grey line between specific rows
          if (data.section === 'body') {
            const { x, y, width } = data.cell;
            
            // Between innskytter and medhjelper rows (row index 1 to 2)
            if (data.row.index === 2) {
              doc.setDrawColor(180, 180, 180); // Light grey
              doc.setLineWidth(0.5);
              doc.line(x, y, x + width, y);
              doc.setDrawColor(0, 0, 0); // Reset to black
            }
            
            // Between athletes in the same time slot (starting from row 3 onwards)
            if (data.row.index >= 3) {
              const rowIndexInData = data.row.index - 3; // Adjust for header rows
              // For odd rows (second athlete), draw light grey line on top
              if (rowIndexInData % 2 === 1) {
                doc.setDrawColor(180, 180, 180); // Light grey
                doc.setLineWidth(0.5);
                doc.line(x, y, x + width, y);
                doc.setDrawColor(0, 0, 0); // Reset to black
              }
            }
          }
        },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto'
      });
      
      // ===== SECOND REPORT: INNPISKERLISTE (PORTRAIT) =====
      // Add new page in portrait orientation
      doc.addPage('a4', 'p');
      pageWidth = doc.internal.pageSize.getWidth();
      rightX = pageWidth - 14;
      
      // Add header information with blue background
      doc.setFillColor(90, 130, 180);
      doc.rect(0, 0, pageWidth, 30, 'F');
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('Innpiskerliste', pageWidth / 2, 12, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      yPos = 20;
      
      // Event information
      if (selectedEvent) {
        doc.text(`Event: ${selectedEvent.Name}`, 14, yPos);
        yPos += 6;
      }
      
      // Reset text color for body
      doc.setTextColor(0, 0, 0);
      yPos = 35;
      
      // Get all assigned participants with their lane numbers
      const assignedParticipants: Array<{
        participant: Participant;
        lane: number;
      }> = [];
      
      const filteredParticipantUIDs = new Set(filteredParticipants.map(p => p.UID));
      
      Object.keys(tableAssignments).forEach(key => {
        const assignment = tableAssignments[key];
        if (assignment && filteredParticipantUIDs.has(assignment.UID)) {
          // Key format is "time-lane-position"
          const parts = key.split('-');
          const lane = parseInt(parts[1]);
          assignedParticipants.push({
            participant: assignment,
            lane
          });
        }
      });
      
      // Sort by start number ascending
      assignedParticipants.sort((a, b) => a.participant.Startnummer - b.participant.Startnummer);
      
      // Prepare table data for innpiskerliste
      const tableData2: any[] = [];
      
      assignedParticipants.forEach(({ participant, lane }) => {
        // Get actual start time from API data using findAttribute helper
        const startTime = findAttribute(participant.EtappeDeltaker, 'StarttidFormatert') || '';
        
        // Get skive number from API data (same as used in skyteplott)
        const skiveNr = participant.Skive?.toString() || '';
        
        // Truncate class name to 8 characters
        const className = participant.Klasse?.Navn || '';
        const truncatedClassName = className.length > 8 ? className.substring(0, 8) : className;
        
        const row = [
          { content: participant.Startnummer.toString(), styles: { fontSize: 10, fontStyle: 'bold' } },
          { content: participant.Utover.NavnFormatert, styles: { fontSize: 10, fontStyle: 'bold' } },
          skiveNr,
          truncatedClassName,
          startTime,
          '', // Våpen motatt
          '', // Ferdig innskutt
          '', // Ferdig Ladet
          '', // Våpen satt ut
        ];
        tableData2.push(row);
      });
      
      // Add total row
      tableData2.push([
        'Total',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        assignedParticipants.length.toString()
      ]);
      
      // Generate second table (innpiskerliste)
      autoTable(doc, {
        startY: yPos,
        head: [[
          { content: 'Star nr', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Navn', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Skive nr', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Klasse', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Start tid', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Våpen motatt', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Ferdig innskutt', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Ferdig Ladet', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Våpen satt ut', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } }
        ]],
        body: tableData2,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 1.5,
          overflow: 'linebreak',
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
          halign: 'center'
        },
        headStyles: {
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        tableWidth: 'auto',
        columnStyles: {
          0: { cellWidth: 12 }, // Star nr
          1: { cellWidth: 50, halign: 'left' }, // Navn
          2: { cellWidth: 12 }, // Skive nr
          3: { cellWidth: 16 }, // Klasse
          4: { cellWidth: 15 }, // Start tid
          5: { cellWidth: 17 }, // Våpen motatt
          6: { cellWidth: 17 }, // Ferdig innskutt
          7: { cellWidth: 27 }, // Ferdig Ladet
          8: { cellWidth: 17 } // Våpen satt ut
        },
        didParseCell: (data: any) => {
          // Alternating row shading (skip header row)
          if (data.section === 'body' && data.row.index < tableData2.length - 1) {
            if (data.row.index % 2 === 0) {
              data.cell.styles.fillColor = [240, 240, 240];
            }
          }
          // Style the total row
          if (data.section === 'body' && data.row.index === tableData2.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [90, 130, 180];
            data.cell.styles.textColor = [255, 255, 255];
          }
        },
        margin: { left: 10, right: 10 }
      });
      
      // ===== THIRD REPORT: SKYTEPLOTT =====
      const skyteplottParticipants = filteredParticipants;
      
      if (skyteplottParticipants.length > 0) {
        // Helper function to get stored shooting direction
        const getStoredDirection = (participantUID: string): 'V' | 'H' | string => {
          try {
            const stored = localStorage.getItem('ShootingDirection');
            if (stored) {
              const data = JSON.parse(stored);
              return data[participantUID] || 'V / H';
            }
          } catch (error) {
            console.error('Error reading ShootingDirection:', error);
          }
          return 'V / H';
        };

        // Prepare participant pairs (2 per row)
        const participantPairs: (Participant | undefined)[][] = [];
        for (let i = 0; i < skyteplottParticipants.length; i += 2) {
          participantPairs.push([
            skyteplottParticipants[i],
            skyteplottParticipants[i + 1]
          ]);
        }

        // Split into pages (6 rows per page)
        const pages: (Participant | undefined)[][][] = [];
        for (let i = 0; i < participantPairs.length; i += 6) {
          pages.push(participantPairs.slice(i, i + 6));
        }

        // Render each page as HTML, capture with html2canvas, and add to PDF
        for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
          const page = pages[pageIndex];
          
          // Create a temporary container
          const container = document.createElement('div');
          container.style.position = 'absolute';
          container.style.left = '-9999px';
          container.style.width = '210mm';
          container.style.backgroundColor = 'white';
          container.style.padding = '5mm 2mm';
          container.style.boxSizing = 'border-box';
          document.body.appendChild(container);

          // Add header
          const header = document.createElement('div');
          header.style.textAlign = 'center';
          header.style.marginBottom = '2mm';
          header.style.paddingBottom = '1mm';
          header.style.borderBottom = '1px solid #333';
          header.style.fontSize = '16px';
          header.style.fontWeight = 'bold';
          header.innerHTML = `${selectedEvent?.Name || 'Event'} - ${selectedEvent ? new Date(selectedEvent.Date).toLocaleDateString('no-NO') : ''}`;
          container.appendChild(header);

          // Render rows
          page.forEach((rowParticipants) => {
            const rowDiv = document.createElement('div');
            rowDiv.style.marginBottom = '1mm';
            rowDiv.style.border = '1px solid #333';
            rowDiv.style.backgroundColor = 'white';

            const table = document.createElement('table');
            table.style.width = '100%';
            table.style.borderCollapse = 'collapse';
            table.style.minHeight = '45mm';

            const tbody = document.createElement('tbody');
            const tr = document.createElement('tr');

            // Helper to create a cell
            const createCell = (participant: Participant | undefined, isLeft: boolean) => {
              const td1 = document.createElement('td');
              td1.style.border = '1px solid #333';
              td1.style.padding = '2mm';
              td1.style.textAlign = 'center';
              td1.style.verticalAlign = 'top';
              td1.style.width = '7%';

              if (participant) {
                // Bib box
                const bibBox = document.createElement('div');
                bibBox.style.width = '24mm';
                bibBox.style.height = '12mm';
                bibBox.style.border = '2px solid #333';
                bibBox.style.display = 'flex';
                bibBox.style.flexDirection = 'column';
                bibBox.style.alignItems = 'center';
                bibBox.style.justifyContent = 'center';
                bibBox.style.backgroundColor = 'white';
                bibBox.style.padding = '1mm';
                bibBox.style.margin = '0 auto';
                bibBox.innerHTML = `
                  <div style="color: #1890ff; text-decoration: underline; font-weight: bold; font-size: 10px; margin-bottom: 1mm;">
                    ${participant.Skive != null ? 'Bib/Lane' : 'Bib'}
                  </div>
                  <div style="font-size: 14px; font-weight: bold; text-align: center;">
                    ${participant.Skive != null ? `${participant.Startnummer} / ${participant.Skive}` : participant.Startnummer}
                  </div>
                `;
                td1.appendChild(bibBox);

                // Direction
                const dirDiv = document.createElement('div');
                dirDiv.style.marginTop = '3mm';
                const direction = getStoredDirection(participant.Utover.UID.toString());
                dirDiv.style.color = direction === 'V' ? '#ff0000' : direction === 'H' ? '#00ff00' : '#1890ff';
                dirDiv.style.fontWeight = 'bold';
                dirDiv.style.fontSize = '18px';
                dirDiv.textContent = direction;
                td1.appendChild(dirDiv);

                // Signature box
                const sigBox = document.createElement('div');
                sigBox.style.width = '24mm';
                sigBox.style.height = '12mm';
                sigBox.style.border = '1px solid #333';
                sigBox.style.marginTop = '3mm';
                sigBox.style.backgroundColor = 'white';
                sigBox.style.margin = '3mm auto 0';
                td1.appendChild(sigBox);
              }

              return td1;
            };

            const createCenterCell = (participant: Participant | undefined) => {
              const td = document.createElement('td');
              td.style.border = '1px solid #333';
              td.style.padding = '2mm';
              td.style.textAlign = 'center';
              td.style.verticalAlign = 'middle';
              td.style.width = '43%';

              if (participant) {
                const wrapper = document.createElement('div');
                wrapper.style.display = 'flex';
                wrapper.style.flexDirection = 'column';
                wrapper.style.alignItems = 'center';

                // Name and time
                const infoDiv = document.createElement('div');
                infoDiv.style.display = 'flex';
                infoDiv.style.justifyContent = 'space-between';
                infoDiv.style.alignItems = 'center';
                infoDiv.style.width = '100%';
                infoDiv.style.marginBottom = '2mm';
                
                const firstName = participant.Utover.Fornavn.split(' ')[0];
                const lastName = participant.Utover.Etternavn;
                let fullName = `${firstName} ${lastName}`;
                if (fullName.length > 25) {
                  fullName = fullName.substring(0, 25) + '...';
                }
                
                const startTime = findAttribute(participant.EtappeDeltaker, 'StarttidFormatert') || 'Unknown';
                
                infoDiv.innerHTML = `
                  <span style="color: #1890ff; font-size: 14px; font-weight: bold;">${fullName}</span>
                  <span style="color: #ff0000; font-size: 14px; font-weight: bold;">${startTime}</span>
                `;
                wrapper.appendChild(infoDiv);

                // Wheels
                const wheelsDiv = document.createElement('div');
                wheelsDiv.style.display = 'flex';
                wheelsDiv.style.gap = '10px';
                
                for (let i = 0; i < 2; i++) {
                  const wheelOuter = document.createElement('div');
                  wheelOuter.style.width = '25mm';
                  wheelOuter.style.height = '25mm';
                  wheelOuter.style.border = '2px solid #333';
                  wheelOuter.style.borderRadius = '50%';
                  wheelOuter.style.display = 'flex';
                  wheelOuter.style.alignItems = 'center';
                  wheelOuter.style.justifyContent = 'center';
                  wheelOuter.style.backgroundColor = 'white';
                  
                  const wheelInner = document.createElement('div');
                  wheelInner.style.width = '12.5mm';
                  wheelInner.style.height = '12.5mm';
                  wheelInner.style.border = '1px solid #333';
                  wheelInner.style.borderRadius = '50%';
                  wheelInner.style.backgroundColor = 'white';
                  
                  wheelOuter.appendChild(wheelInner);
                  wheelsDiv.appendChild(wheelOuter);
                }
                
                wrapper.appendChild(wheelsDiv);
                td.appendChild(wrapper);
              }

              return td;
            };

            // Add cells to row
            tr.appendChild(createCell(rowParticipants[0], true));
            tr.appendChild(createCenterCell(rowParticipants[0]));
            tr.appendChild(createCenterCell(rowParticipants[1]));
            tr.appendChild(createCell(rowParticipants[1], false));

            tbody.appendChild(tr);
            table.appendChild(tbody);
            rowDiv.appendChild(table);
            container.appendChild(rowDiv);
          });

          // Capture with html2canvas and add to PDF
          try {
            const canvas = await html2canvas(container, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: '#ffffff'
            });
            
            // Remove temporary container
            document.body.removeChild(container);
            
            // Add page to PDF
            doc.addPage('a4', 'p');
            const imgData = canvas.toDataURL('image/png');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * pageWidth) / canvas.width;
            
            doc.addImage(imgData, 'PNG', 0, 0, imgWidth, Math.min(imgHeight, pageHeight));
          } catch (error) {
            console.error('Error capturing Skyteplott page:', error);
            document.body.removeChild(container);
          }
        }
      }
      
      // Save the combined PDF
      const groupPart = availableGroups.length > 1 && puljename ? `${puljename}-` : '';
      const fileName = `${groupPart}${selectedEvent?.Name || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      message.success('Combined PDF with all three reports generated successfully!');
    } catch (error) {
      console.error('Error generating combined PDF:', error);
      message.error('Failed to generate combined PDF');
    }
  };

  const handleDownloadInnpiskerliste = () => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4'); // portrait orientation
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Add header information with blue background
      doc.setFillColor(90, 130, 180);
      doc.rect(0, 0, pageWidth, 30, 'F');
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('Innpiskerliste', pageWidth / 2, 12, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      let yPos = 20;
      
      // Event information
      if (selectedEvent) {
        doc.text(`Event: ${selectedEvent.Name}`, 14, yPos);
        yPos += 6;
      }
      
      // Reset text color for body
      doc.setTextColor(0, 0, 0);
      yPos = 35;
      
      // Get all assigned participants with their lane numbers
      // Filter out innskyttere and medhjelper by only including actual participants
      const assignedParticipants: Array<{
        participant: Participant;
        lane: number;
      }> = [];
      
      const filteredParticipantUIDs = new Set(filteredParticipants.map(p => p.UID));
      
      Object.keys(tableAssignments).forEach(key => {
        const assignment = tableAssignments[key];
        if (assignment && filteredParticipantUIDs.has(assignment.UID)) {
          // Key format is "time-lane-position"
          const parts = key.split('-');
          const lane = parseInt(parts[1]);
          assignedParticipants.push({
            participant: assignment,
            lane
          });
        }
      });
      
      // Sort by start number ascending
      assignedParticipants.sort((a, b) => a.participant.Startnummer - b.participant.Startnummer);
      
      // Prepare table data
      const tableData: any[] = [];
      
      assignedParticipants.forEach(({ participant, lane }) => {
        // Get actual start time from API data using findAttribute helper
        const startTime = findAttribute(participant.EtappeDeltaker, 'StarttidFormatert') || '';
        
        // Get skive number from API data (same as used in skyteplott)
        const skiveNr = participant.Skive?.toString() || '';
        
        // Truncate class name to 8 characters
        const className = participant.Klasse?.Navn || '';
        const truncatedClassName = className.length > 8 ? className.substring(0, 8) : className;
        
        const row = [
          { content: participant.Startnummer.toString(), styles: { fontSize: 10, fontStyle: 'bold' } },
          { content: participant.Utover.NavnFormatert, styles: { fontSize: 10, fontStyle: 'bold' } },
          skiveNr,
          truncatedClassName,
          startTime,
          '', // Våpen motatt
          '', // Ferdig innskutt
          '', // Ferdig Ladet
          '', // Våpen satt ut
        ];
        tableData.push(row);
      });
      
      // Add total row
      tableData.push([
        'Total',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        assignedParticipants.length.toString()
      ]);
      
      // Generate table with autoTable
      autoTable(doc, {
        startY: yPos,
        head: [[
          { content: 'Star nr', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Navn', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Skive nr', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Klasse', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Start tid', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Våpen motatt', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Ferdig innskutt', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Ferdig Ladet', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: 'Våpen satt ut', styles: { fillColor: [90, 130, 180], textColor: [255, 255, 255], fontStyle: 'bold' } }
        ]],
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 1.5,
          overflow: 'linebreak',
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
          halign: 'center'
        },
        headStyles: {
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        tableWidth: 'auto',
        columnStyles: {
          0: { cellWidth: 12 }, // Star nr
          1: { cellWidth: 50, halign: 'left' }, // Navn
          2: { cellWidth: 12 }, // Skive nr
          3: { cellWidth: 16 }, // Klasse
          4: { cellWidth: 15 }, // Start tid
          5: { cellWidth: 17 }, // Våpen motatt
          6: { cellWidth: 17 }, // Ferdig innskutt
          7: { cellWidth: 27 }, // Ferdig Ladet
          8: { cellWidth: 17 } // Våpen satt ut
        },
        didParseCell: (data: any) => {
          // Alternating row shading (skip header row)
          if (data.section === 'body' && data.row.index < tableData.length - 1) {
            if (data.row.index % 2 === 0) {
              data.cell.styles.fillColor = [240, 240, 240];
            }
          }
          // Style the total row
          if (data.section === 'body' && data.row.index === tableData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [90, 130, 180];
            data.cell.styles.textColor = [255, 255, 255];
          }
        },
        margin: { left: 10, right: 10 }
      });
      
      // Save the PDF
      const fileName = `innpiskerliste-${selectedEvent?.Name || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      message.success('Innpiskerliste PDF generated successfully!');
    } catch (error) {
      console.error('Error generating innpiskerliste PDF:', error);
      message.error('Failed to generate innpiskerliste PDF');
    }
  };

  const handleFilterOk = () => {
    setFilterModalVisible(false);
  };

  const handleFilterCancel = () => {
    setFilterModalVisible(false);
  };

  const handleFilterReset = () => {
    setSelectedClubs([]);
    setSelectedTeams([]);
    setSelectedKretser([]);
    setSelectedClasses([]);
    setNameFilter('');
    setExcludeBibs('');
    setStartTimeFrom('');
    setStartTimeTo('');
  };

  const handleSettingsOk = async () => {
    const oldPuljename = puljename;
    const newPuljename = tempPuljename;
    const isRenaming = oldPuljename !== newPuljename;
    
    console.log('=== SETTINGS OK START ===');
    console.log('oldPuljename:', oldPuljename);
    console.log('newPuljename:', newPuljename);
    console.log('isRenaming:', isRenaming);
    console.log('selectedEvent:', selectedEvent?.Id);
    console.log('selectedProfileId:', selectedProfileId);
    
    // Set loading flag during rename to prevent autosave
    if (isRenaming) {
      console.log('Setting isLoadingGroup=true and isRenamingRef=true to prevent autosave during rename');
      setIsLoadingGroup(true);
      isRenamingRef.current = true;
    }
    
    setStartInnskyting(tempStartInnskyting);
    setSisteInnskyting(tempSisteInnskyting);
    setForsteSkive(tempForsteSkive);
    setSisteSkive(tempSisteSkive);
    setSkiveData(tempSkiveData);
    setTidPerPar(tempTidPerPar);
    setShowSkiveDetails(tempShowSkiveDetails);
    setUtdelingStartnummerAmmo(tempUtdelingStartnummerAmmo);
    setInnpiskerTilInnskyting(tempInnpiskerTilInnskyting);
    setPappBytteFra(tempPappBytteFra);
    setPappBytteTil(tempPappBytteTil);
    setAntallAndreUttovere(tempAntallAndreUttovere);
    setAntallInnskyttere(tempAntallInnskyttere);
    setPuljename(newPuljename);
    
    setSettingsModalVisible(false);
    
    // Handle rename: delete old entry and save to new name
    if (isRenaming && selectedEvent && user && selectedProfileId) {
      console.log('ENTERING RENAME BLOCK');
      try {
        const profileRef = doc(db, 'profiles', selectedProfileId);
        const profileDoc = await getDoc(profileRef);
        
        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          const eventSetups = profileData.setupData?.[selectedEvent.Id] || {};
          
          console.log('Current event setups keys:', Object.keys(eventSetups));
          console.log('Looking for oldPuljename:', oldPuljename);
          
          // Copy old data to new name and remove old entry
          const { [oldPuljename]: oldData, ...remainingSetups } = eventSetups;
          
          console.log('oldData found:', !!oldData);
          console.log('remainingSetups keys:', Object.keys(remainingSetups));
          
          if (oldData) {
            // Update the puljename in the setup data
            const updatedOldData = {
              ...oldData,
              setup: {
                ...oldData.setup,
                puljename: newPuljename,
                // Update with the new temp values
                startInnskyting: tempStartInnskyting?.format('HH:mm') || null,
                sisteInnskyting: tempSisteInnskyting?.format('HH:mm') || null,
                forsteSkive: tempForsteSkive,
                sisteSkive: tempSisteSkive,
                skiveData: tempSkiveData,
                tidPerPar: tempTidPerPar,
                showSkiveDetails: tempShowSkiveDetails,
                utdelingStartnummerAmmo: tempUtdelingStartnummerAmmo,
                innpiskerTilInnskyting: tempInnpiskerTilInnskyting,
                pappBytteFra: tempPappBytteFra?.format('HH:mm') || null,
                pappBytteTil: tempPappBytteTil?.format('HH:mm') || null,
                antallAndreUttovere: tempAntallAndreUttovere,
                antallInnskyttere: tempAntallInnskyttere
              }
            };
            
            // Save with new name, removing old name
            const updatedSetupData = {
              ...profileData.setupData,
              [selectedEvent.Id]: {
                ...remainingSetups,
                [newPuljename]: updatedOldData
              }
            };
            
            console.log('About to save to Firebase. Updated setup keys:', Object.keys(updatedSetupData[selectedEvent.Id]));
            
            await setDoc(profileRef, {
              ...profileData,
              setupData: updatedSetupData,
              updatedAt: new Date().toISOString()
            });
            
            console.log('Firebase update complete');
            
            // Update availableGroups list
            setAvailableGroups(groups => 
              groups.map(g => g === oldPuljename ? newPuljename : g).sort()
            );
            
            console.log('AvailableGroups updated');
            
            message.success(`Renamed "${oldPuljename}" to "${newPuljename}"`);
            
            // Clear loading flag after a short delay to ensure state updates complete
            setTimeout(() => {
              console.log('Clearing isLoadingGroup flag and isRenamingRef after rename');
              setIsLoadingGroup(false);
              isRenamingRef.current = false;
            }, 100);
          } else {
            console.log('oldData not found - cannot rename');
            setIsLoadingGroup(false);
            isRenamingRef.current = false;
          }
        }
      } catch (error) {
        console.error('Error renaming pulje:', error);
        message.error('Failed to rename pulje');
        setIsLoadingGroup(false);
        isRenamingRef.current = false;
      }
    } else {
      console.log('NOT RENAMING - calling regular saveSetup');
      console.log('Reason: isRenaming=', isRenaming, 'selectedEvent=', !!selectedEvent, 'user=', !!user, 'selectedProfileId=', !!selectedProfileId);
      // Only save if not renaming (regular settings update)
      setTimeout(() => {
        console.log('Executing saveSetup with puljename:', newPuljename);
        const setupData: InnskytingslisteSetupData = {
          startInnskyting: tempStartInnskyting?.format('HH:mm') || null,
          sisteInnskyting: tempSisteInnskyting?.format('HH:mm') || null,
          forsteSkive: tempForsteSkive,
          sisteSkive: tempSisteSkive,
          skiveData: tempSkiveData,
          tidPerPar: tempTidPerPar,
          tableAssignments,
          blockedCells,
          showSkiveDetails: tempShowSkiveDetails,
          utdelingStartnummerAmmo: tempUtdelingStartnummerAmmo,
          innpiskerTilInnskyting: tempInnpiskerTilInnskyting,
          pappBytteFra: tempPappBytteFra?.format('HH:mm') || null,
          pappBytteTil: tempPappBytteTil?.format('HH:mm') || null,
          antallAndreUttovere: tempAntallAndreUttovere,
          antallInnskyttere: tempAntallInnskyttere,
          puljename: newPuljename
        };
        saveSetup(setupData);
      }, 0);
    }
  };

  const handleSettingsCancel = () => {
    setTempStartInnskyting(startInnskyting);
    setTempSisteInnskyting(sisteInnskyting);
    setTempForsteSkive(forsteSkive);
    setTempSisteSkive(sisteSkive);
    setTempSkiveData(skiveData);
    setTempTidPerPar(tidPerPar);
    setTempShowSkiveDetails(showSkiveDetails);
    setTempUtdelingStartnummerAmmo(utdelingStartnummerAmmo);
    setTempInnpiskerTilInnskyting(innpiskerTilInnskyting);
    setTempPappBytteFra(pappBytteFra);
    setTempPappBytteTil(pappBytteTil);
    setTempAntallAndreUttovere(antallAndreUttovere);
    setTempAntallInnskyttere(antallInnskyttere);
    setTempPuljename(puljename);
    
    setSettingsModalVisible(false);
  };

  const handleCalculateTimePerPair = () => {
    const numberOfLanes = tempSisteSkive - tempForsteSkive + 1;
    const filteredParticipants = getFilteredParticipants();
    const totalAthletes = filteredParticipants.length + tempAntallAndreUttovere;
    const numberOfSlotsNeeded = Math.ceil(totalAthletes / numberOfLanes)*numberOfLanes;
    if (!tempStartInnskyting || !tempSisteInnskyting || numberOfSlotsNeeded === 0) {
      // Todo make sure the calculate button is not clickable if these conditions are not met
      return;
    }

    let totalMinutes = tempSisteInnskyting.diff(tempStartInnskyting, 'minute');
    
    // Subtract pappbytte duration from available time
    if (tempPappBytteFra && tempPappBytteTil) {
      const pappbytteDuration = tempPappBytteTil.diff(tempPappBytteFra, 'minute');
      totalMinutes -= pappbytteDuration;
    }
    
    if (totalMinutes <= 0) {
      //todo: Add a message
      return;
    }

    const timePerPair = Math.floor((totalMinutes * numberOfLanes * 2) / numberOfSlotsNeeded) || 0;
    setTempTidPerPar(timePerPair.toString());
  };

  const handleExportSetup = () => {
    try {
      const setupData: InnskytingslisteSetupData = {
        startInnskyting: startInnskyting?.format('HH:mm') || null,
        sisteInnskyting: sisteInnskyting?.format('HH:mm') || null,
        forsteSkive,
        sisteSkive,
        skiveData,
        tidPerPar,
        tableAssignments,
        blockedCells,
        showSkiveDetails,
        utdelingStartnummerAmmo,
        innpiskerTilInnskyting,
        pappBytteFra: pappBytteFra?.format('HH:mm') || null,
        pappBytteTil: pappBytteTil?.format('HH:mm') || null,
        antallAndreUttovere,
        antallInnskyttere,
        puljename
      };
      
      const dataStr = JSON.stringify(setupData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `innskytingsliste-setup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success('Setup exported successfully!');
    } catch (error) {
      console.error('Error exporting setup:', error);
      message.error('Failed to export setup');
    }
  };

  const handleImportSetup = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importedData = JSON.parse(content);
        
        if (typeof importedData === 'object' && importedData !== null) {
          setStartInnskyting(importedData.startInnskyting ? dayjs(importedData.startInnskyting, 'HH:mm') : null);
          setSisteInnskyting(importedData.sisteInnskyting ? dayjs(importedData.sisteInnskyting, 'HH:mm') : null);
          setForsteSkive(importedData.forsteSkive || 1);
          setSisteSkive(importedData.sisteSkive || 1);
          setSkiveData(importedData.skiveData || {});
          setTidPerPar(importedData.tidPerPar || '');
          setTableAssignments(importedData.tableAssignments || {});
          
          message.success('Setup imported successfully!');
        } else {
          message.error('Invalid file format: expected JSON object');
        }
      } catch (error) {
        console.error('Error importing setup:', error);
        message.error('Failed to import setup: invalid JSON file');
      }
    };
    reader.readAsText(file);
    return false;
  };

  const getFilteredParticipants = useCallback(() => {
    return participants.filter(participant => {
      if (nameFilter) {
        const fullName = `${participant.Utover.Fornavn} ${participant.Utover.Etternavn}`.toLowerCase();
        if (!fullName.includes(nameFilter.toLowerCase())) {
          return false;
        }
      }

      if (excludeBibs) {
        const excludedBibNumbers = excludeBibs
          .split(';')
          .map(bib => bib.trim())
          .filter(bib => bib !== '')
          .map(bib => parseInt(bib, 10))
          .filter(bib => !isNaN(bib));
        
        if (excludedBibNumbers.includes(participant.Startnummer)) {
          return false;
        }
      }

      if (selectedClubs.length > 0) {
        if (!selectedClubs.includes(participant.Utover.Klubb.Navn)) {
          return false;
        }
      }

      if (selectedTeams.length > 0) {
        const teamName = participant.Utover.Team?.Navn;
        if (!teamName || !selectedTeams.includes(teamName)) {
          return false;
        }
      }

      if (selectedKretser.length > 0) {
        const kretsName = participant.Region?.Navn;
        if (!kretsName || !selectedKretser.includes(kretsName)) {
          return false;
        }
      }

      if (selectedClasses.length > 0) {
        if (!selectedClasses.includes(participant.Klasse.Navn)) {
          return false;
        }
      }

      if (startTimeFrom || startTimeTo) {
        const startTime = findAttribute(participant.EtappeDeltaker, 'StarttidFormatert');
        if (startTime) {
          const participantTime = startTime.substring(0, 5);
          if (startTimeFrom && participantTime < startTimeFrom) {
            return false;
          }
          if (startTimeTo && participantTime > startTimeTo) {
            return false;
          }
        }
      }

      return true;
    });
  }, [participants, nameFilter, excludeBibs, selectedClubs, selectedTeams, selectedKretser, selectedClasses, startTimeFrom, startTimeTo]);

  const getHeaderData = useCallback(() => {
    const filteredParticipants = getFilteredParticipants();
    const numberOfSkiver = sisteSkive - forsteSkive + 1;
    const reserveInnskytingerPerSkive = 1;
    
    const timePerPair = parseFloat(tidPerPar) || 0;
    const timePerAthleteMinutes = timePerPair / 2;
    const minutes = Math.floor(timePerAthleteMinutes);
    const seconds = Math.round((timePerAthleteMinutes - minutes) * 60);
    const timePerAthleteFormatted = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    
    let antallMinTilHver = '';
    if (startInnskyting && sisteInnskyting && numberOfSkiver > 0) {
      const totalMinutes = sisteInnskyting.diff(startInnskyting, 'minute');
      const timePerLane = totalMinutes / numberOfSkiver;
      antallMinTilHver = `${Math.floor(timePerLane / 60).toString().padStart(2, '0')}:${(timePerLane % 60).toString().padStart(2, '0')}`;
    }
    
    return {
      eventName: selectedEvent?.Name || 'Unknown Event',
      eventLocation: selectedEvent?.City?.Name || 'Unknown Location',
      antallDeltagere: filteredParticipants.length,
      reserveInnskytingerPerSkive,
      innskytingBegynner: startInnskyting?.format('HH:mm') || '',
      innskytingAvslutter: sisteInnskyting?.format('HH:mm') || '',
      antallSkiver: numberOfSkiver,
      antallMinTilHver,
      minForKvartPar: tidPerPar,
      timePerAthleteMinutes,
      timePerAthleteFormatted,
      utdelingStartnummerAmmo: utdelingStartnummerAmmo || '',
      innpiskerTilInnskyting: innpiskerTilInnskyting || ''
    };
  }, [selectedEvent, getFilteredParticipants, sisteSkive, forsteSkive, startInnskyting, sisteInnskyting, tidPerPar, utdelingStartnummerAmmo, innpiskerTilInnskyting]);

  useEffect(() => {
    const numberOfSkiver = sisteSkive - forsteSkive + 1;
    const maxAthletesPerLane = 16;
    const maxParticipantRows = maxAthletesPerLane * numberOfSkiver;
    
    const dragDropTable = document.querySelector('table tbody');
    const actualDragDropRows = dragDropTable ? dragDropTable.querySelectorAll('tr').length : 10;
    
    const totalDragDropRows = Math.max(actualDragDropRows, 10);
    
    document.documentElement.style.setProperty('--row-count', maxParticipantRows.toString());
    document.documentElement.style.setProperty('--estimated-rows', totalDragDropRows.toString());
    document.documentElement.style.setProperty('--skive-count', numberOfSkiver.toString());
    
    console.log(`Dynamic print sizing: max ${maxParticipantRows} participant rows (${maxAthletesPerLane} per lane × ${numberOfSkiver} lanes), ${totalDragDropRows} drag-drop rows`);
  }, [sisteSkive, forsteSkive, startInnskyting, sisteInnskyting]);

  const getStartTime = useCallback((participant: Participant) => {
    if (participant.EtappeDeltaker && (participant.EtappeDeltaker.length > 0 || true)) {
      const _part = participant;
      const _ed = _part.EtappeDeltaker;
      const starttid = findAttribute(_ed, 'StarttidFormatert');
      return starttid || 'Unknown'; 
    }
    return 'Unknown';
  }, []);

  const isParticipantAssigned = useCallback((participant: Participant) => {
    return Object.values(tableAssignments).some(assigned => assigned?.UID === participant.UID);
  }, [tableAssignments]);

  const getParticipantAssignment = useCallback((participant: Participant) => {
    const assignedCell = Object.keys(tableAssignments).find(key => 
      tableAssignments[key]?.UID === participant.UID
    );
    
    if (!assignedCell) {
      return null;
    }
    
    const parts = assignedCell.split('-');
    if (parts.length >= 2) {
      const time = parts[0];
      const skive = parts[1];
      return { time, lane: skive };
    }
    
    return null;
  }, [tableAssignments]);

  const filteredParticipants = getFilteredParticipants();

  const getCalculateButtonState = useCallback(() => {
    const hasParticipants = filteredParticipants.length > 0;
    const hasTimes = tempStartInnskyting && tempSisteInnskyting;
    
    if (!hasParticipants && !hasTimes) {
      return {
        disabled: true,
        tooltip: 'Kan ikke beregne: Mangler utøvere og start-/sluttider'
      };
    } else if (!hasParticipants) {
      return {
        disabled: true,
        tooltip: 'Kan ikke beregne: Ingen utøvere i filtrert liste'
      };
    } else if (!hasTimes) {
      return {
        disabled: true,
        tooltip: 'Kan ikke beregne: Mangler start- og/eller sluttid for innzeroing'
      };
    }
    
    return {
      disabled: false,
      tooltip: ''
    };
  }, [filteredParticipants.length, tempStartInnskyting, tempSisteInnskyting]);

  const skiveNumbers = useMemo(() => {
    console.log('Generating skive numbers from', forsteSkive, 'to', sisteSkive);
    const numbers = [];
    for (let i = sisteSkive; i >= forsteSkive; i--) {
      numbers.push(i);
    }
    console.log('Generated skive numbers:', numbers);
    return numbers;
  }, [forsteSkive, sisteSkive]);

  const timeSlots = useMemo(() => {
    console.log('Generating time slots from', startInnskyting?.format('HH:mm'), 'to', sisteInnskyting?.format('HH:mm'), 'with tidPerPar:', tidPerPar);
    
    if (!startInnskyting || !sisteInnskyting || !tidPerPar) {
      console.log('Missing setup values, using fallback time slots');
      return ['10:00', '10:14', '10:28', '10:42', '10:56'];
    }
    
    const slots = [];
    const timePerPairMinutes = parseFloat(tidPerPar.replace('min', '').trim());
    if (isNaN(timePerPairMinutes)) {
      console.log('Invalid tidPerPar value, using fallback time slots');
      return ['10:00', '10:14', '10:28', '10:42', '10:56'];
    }
    
    let currentTime = startInnskyting.clone();
    let pappbytteApplied = false;
    
    while (currentTime.isBefore(sisteInnskyting)) {
      slots.push(currentTime.format('HH:mm'));
      
      // Check if pappbytte occurs during this slot
      if (!pappbytteApplied && pappBytteFra && pappBytteTil) {
        const bytteFraTime = pappBytteFra;
        const nextSlotTime = currentTime.add(timePerPairMinutes, 'minute');
        
        // If pappbytte starts during this slot (after current time, before next slot)
        if ((currentTime.isSame(bytteFraTime, 'minute') || currentTime.isBefore(bytteFraTime, 'minute')) &&
            bytteFraTime.isBefore(nextSlotTime, 'minute')) {
          const pappbytteDuration = pappBytteTil.diff(pappBytteFra, 'minute');
          // Add both normal slot duration AND pappbytte duration
          currentTime = currentTime.add(timePerPairMinutes + pappbytteDuration, 'minute');
          pappbytteApplied = true;
          continue;
        }
      }
      
      currentTime = currentTime.add(timePerPairMinutes, 'minute');
    }
    
    console.log('Generated time slots:', slots);
    return slots.length > 0 ? slots : ['10:00', '10:14', '10:28', '10:42', '10:56'];
  }, [startInnskyting, sisteInnskyting, tidPerPar, pappBytteFra, pappBytteTil]);

  const getPappbytteInfo = useCallback((timeSlot: string, nextTimeSlot: string | undefined) => {
    if (!pappBytteFra || !pappBytteTil || !tidPerPar) {
      return { isPappbytte: false, duration: 0, extendedEndTime: null, pappbytteStart: null, pappbytteEnd: null };
    }

    const slotTime = dayjs(timeSlot, 'HH:mm');
    const nextSlot = nextTimeSlot ? dayjs(nextTimeSlot, 'HH:mm') : null;
    const bytteFraTime = pappBytteFra;
    const byttetilTime = pappBytteTil;

    // Check if pappbytte starts during this time slot (at or after slot start, before next slot)
    const pappbytteStartsInSlot = 
      (slotTime.isSame(bytteFraTime, 'minute') || slotTime.isBefore(bytteFraTime, 'minute')) &&
      (!nextSlot || bytteFraTime.isBefore(nextSlot, 'minute'));

    if (pappbytteStartsInSlot) {
      const timePerPairMinutes = parseFloat(tidPerPar.replace('min', '').trim());
      const pappbytteDuration = byttetilTime.diff(bytteFraTime, 'minute');
      
      // Calculate the extended end time (normal slot time + pappbytte duration)
      const extendedEndTime = slotTime.add(timePerPairMinutes + pappbytteDuration, 'minute');
      
      return { 
        isPappbytte: true, 
        duration: pappbytteDuration,
        extendedEndTime: extendedEndTime.format('HH:mm'),
        pappbytteStart: bytteFraTime.format('HH:mm'),
        pappbytteEnd: byttetilTime.format('HH:mm')
      };
    }

    return { isPappbytte: false, duration: 0, extendedEndTime: null, pappbytteStart: null, pappbytteEnd: null };
  }, [pappBytteFra, pappBytteTil, tidPerPar]);



  const isLaneFull = useCallback((lane: number) => {
    for (const time of timeSlots) {
      const cellKey1 = `${time}-${lane}-1`;
      const cellKey2 = `${time}-${lane}-2`;
      if (!tableAssignments[cellKey1] || !tableAssignments[cellKey2]) {
        return false;
      }
    }
    return true;
  }, [timeSlots, tableAssignments]);

  const findFirstAvailableCell = useCallback((lane: number): string | null => {
    for (const time of timeSlots) {
      const cellKey1 = `${time}-${lane}-1`;
      const cellKey2 = `${time}-${lane}-2`;
      
      if (!tableAssignments[cellKey1] && !blockedCells[cellKey1]) {
        return cellKey1;
      }
      if (!tableAssignments[cellKey2] && !blockedCells[cellKey2]) {
        return cellKey2;
      }
    }
    return null;
  }, [timeSlots, tableAssignments, blockedCells]);

  const findFirstAvailableCellAnyLane = useCallback((): string | null => {
    for (const time of timeSlots) {
      for (const lane of skiveNumbers) {
        const cellKey1 = `${time}-${lane}-1`;
        const cellKey2 = `${time}-${lane}-2`;
        
        if (!tableAssignments[cellKey1]) {
          return cellKey1;
        }
        if (!tableAssignments[cellKey2]) {
          return cellKey2;
        }
      }
    }
    return null;
  }, [timeSlots, skiveNumbers, tableAssignments]);

  const handleAddToLane = useCallback((participant: Participant, lane: number) => {
    const cellKey = findFirstAvailableCell(lane);
    
    if (cellKey) {
      setTableAssignments(prevAssignments => {
        const newAssignments = { ...prevAssignments };
        newAssignments[cellKey] = participant;
        return newAssignments;
      });
    }
  }, [findFirstAvailableCell]);

  const handleAssignInnskytterToLane = useCallback((innskytterName: string) => {
    // Find first available lane (skive) that doesn't have an innskytter assigned
    for (let skive = tempForsteSkive; skive <= tempSisteSkive; skive++) {
      if (!tempSkiveData[skive]?.innskytter) {
        setTempSkiveData(prev => ({
          ...prev,
          [skive]: {
            innskytter: innskytterName,
            innskytter2: prev[skive]?.innskytter2 || '',
            medhjelper: prev[skive]?.medhjelper || ''
          }
        }));
        return;
      } else if (tempAntallInnskyttere === 2 && !tempSkiveData[skive]?.innskytter2) {
        setTempSkiveData(prev => ({
          ...prev,
          [skive]: {
            ...prev[skive],
            innskytter2: innskytterName
          }
        }));
        return;
      }
    }
    message.warning(tempAntallInnskyttere === 2 ? 'All lanes already have 2 innskyttere assigned' : 'All lanes already have innskyttere assigned');
  }, [tempForsteSkive, tempSisteSkive, tempSkiveData, tempAntallInnskyttere]);

  // Helper function to find which innskytter an athlete belongs to and their assigned lane
  const getInnskytterLaneForAthlete = useCallback((athleteUID: number): { innskytter: Innskytter; lane: number } | null => {
    // Find if athlete belongs to any innskytter
    const innskytter = innskyttereData.find(ins => 
      ins.athletes && ins.athletes.some(athlete => athlete.uid === athleteUID)
    );
    
    if (!innskytter) return null;
    
    // Find which lane this innskytter is assigned to (check both innskytter and innskytter2)
    const assignedLane = skiveNumbers.find(lane => 
      skiveData[lane]?.innskytter === innskytter.name || 
      skiveData[lane]?.innskytter2 === innskytter.name
    );
    
    if (!assignedLane) return null;
    
    return { innskytter, lane: assignedLane };
  }, [innskyttereData, skiveData, skiveNumbers]);

  useEffect(() => {
    const validCellKeys = new Set<string>();
    
    skiveNumbers.forEach((skive: number) => {
      validCellKeys.add(`innskytter-${skive}`);
      validCellKeys.add(`medhjelper-${skive}`);
      timeSlots.forEach((time: string) => {
        validCellKeys.add(`${time}-${skive}-1`);
        validCellKeys.add(`${time}-${skive}-2`);
      });
    });
    
    setTableAssignments(prev => {
      const cleaned: {[key: string]: Participant | null} = {};
      Object.keys(prev).forEach(key => {
        if (validCellKeys.has(key)) {
          cleaned[key] = prev[key];
        }
      });
      return cleaned;
    });
  }, [skiveNumbers, timeSlots]);

  useEffect(() => {
    setTableAssignments(prev => {
      const updated = { ...prev };
      
      skiveNumbers.forEach((skive: number) => {
        const innskytterKey = `innskytter-${skive}`;
        const medhjelperKey = `medhjelper-${skive}`;
        
        if (skiveData[skive]?.innskytter && !updated[innskytterKey]) {
          updated[innskytterKey] = {
            UID: `innskytter-${skive}-auto`,
            Startnummer: 0,
            Utover: {
              Fornavn: skiveData[skive].innskytter,
              Etternavn: '',
              UID: 0,
              Kjonn: '',
              Klubb: { UID: 0, Navn: '', SAID: 0 },
              Team: { UID: 0, Navn: '', SAID: null },
              Land: { UID: 0, Navn: '', ISO2: '', ISO3: '', Fifa: '', IOC: '', Longitude: null, Latitude: null },
              Klubbnavn: '',
              NavnFormatert: skiveData[skive].innskytter,
              Alder: 0,
              Aar: 0,
              Enhet: null,
              NotDuplicates: null
            }
          } as any;
        }
        
        if (skiveData[skive]?.medhjelper && !updated[medhjelperKey]) {
          updated[medhjelperKey] = {
            UID: `medhjelper-${skive}-auto`,
            Startnummer: 0,
            Utover: {
              Fornavn: skiveData[skive].medhjelper,
              Etternavn: '',
              UID: 0,
              Kjonn: '',
              Klubb: { UID: 0, Navn: '', SAID: 0 },
              Team: { UID: 0, Navn: '', SAID: null },
              Land: { UID: 0, Navn: '', ISO2: '', ISO3: '', Fifa: '', IOC: '', Longitude: null, Latitude: null },
              Klubbnavn: '',
              NavnFormatert: skiveData[skive].medhjelper,
              Alder: 0,
              Aar: 0,
              Enhet: null,
              NotDuplicates: null
            }
          } as any;
        }
      });
      
      return updated;
    });
  }, [skiveData, skiveNumbers]);



  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, cellKey: string) => {
    e.preventDefault();
    
    const currentDraggedParticipant = draggedParticipant;
    console.log('Drop event - cellKey:', cellKey, 'draggedParticipant:', currentDraggedParticipant?.Utover?.Fornavn);
    
    if (currentDraggedParticipant) {
      const originalCell = Object.keys(tableAssignments).find(key => 
        tableAssignments[key]?.UID === currentDraggedParticipant.UID
      );
      
      const getCellType = (key: string) => {
        if (key.startsWith('innskytter-')) return 'innskytter';
        if (key.startsWith('medhjelper-')) return 'medhjelper';
        return 'utover';
      };
      
      const originalCellType = originalCell ? getCellType(originalCell) : 'utover';
      const targetCellType = getCellType(cellKey);
      
      if (originalCellType !== targetCellType) {
        console.log('Cross-category drag-and-drop prevented:', originalCellType, 'to', targetCellType);
        setDraggedParticipant(null);
        return;
      }
      
      setTableAssignments(prevAssignments => {
        const currentAssignments = { ...prevAssignments };
        const existingParticipant = currentAssignments[cellKey];
        
        Object.keys(currentAssignments).forEach(key => {
          if (currentAssignments[key]?.UID === currentDraggedParticipant.UID) {
            currentAssignments[key] = null;
          }
        });
        
        if (existingParticipant) {
          const draggedParticipantOriginalCell = Object.keys(prevAssignments).find(key => 
            prevAssignments[key]?.UID === currentDraggedParticipant.UID
          );
          
          if (draggedParticipantOriginalCell) {
            currentAssignments[draggedParticipantOriginalCell] = existingParticipant;
            console.log('Swapped participants:', currentDraggedParticipant.Utover.Fornavn, 'with', existingParticipant.Utover.Fornavn);
          }
        }
        
        currentAssignments[cellKey] = currentDraggedParticipant;
        console.log('Assignment completed - cellKey:', cellKey, 'participant:', currentDraggedParticipant.Utover.Fornavn);
        return currentAssignments;
      });
      setDraggedParticipant(null);
    }
  }, [draggedParticipant, tableAssignments]);

  const handleCellDragStart = useCallback((e: React.DragEvent, participant: Participant) => {
    setDraggedParticipant(participant);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
  }, [setDraggedParticipant]);

  const handleEmptyTable = useCallback(() => {
    setTableAssignments({});
  }, []);

  const handleRemoveParticipant = useCallback((participant: Participant) => {
    setTableAssignments(prevAssignments => {
      const newAssignments = { ...prevAssignments };
      Object.keys(newAssignments).forEach(key => {
        if (newAssignments[key]?.UID === participant.UID) {
          newAssignments[key] = null;
        }
      });
      return newAssignments;
    });
  }, []);

  const handleDropOutsideTable = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const currentDraggedParticipant = draggedParticipant;
    
    if (currentDraggedParticipant) {
      handleRemoveParticipant(currentDraggedParticipant);
      setDraggedParticipant(null);
      console.log('Participant removed from table:', currentDraggedParticipant.Utover.Fornavn);
    }
  }, [draggedParticipant, handleRemoveParticipant]);

  const handleDragOverOutside = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleAddAllFromList = useCallback(() => {
    const filteredParticipants = getFilteredParticipants();
    
    const sortedParticipants = [...filteredParticipants].sort((a, b) => {
      const timeA = getStartTime(a);
      const timeB = getStartTime(b);
      return timeA.localeCompare(timeB);
    });

    setTableAssignments(prevAssignments => {
      const newAssignments: {[key: string]: Participant | null} = { ...prevAssignments };
      let participantIndex = 0;

      // Step 1: Initial assignment - fill slots sequentially (skip blocked cells)
      for (const time of timeSlots) {
        for (const skive of skiveNumbers) {
          const cellKey1 = `${time}-${skive}-1`;
          if (!blockedCells[cellKey1] && participantIndex < sortedParticipants.length) {
            newAssignments[cellKey1] = sortedParticipants[participantIndex];
            participantIndex++;
          }
          
          const cellKey2 = `${time}-${skive}-2`;
          if (!blockedCells[cellKey2] && participantIndex < sortedParticipants.length) {
            newAssignments[cellKey2] = sortedParticipants[participantIndex];
            participantIndex++;
          }
        }
      }

      // Step 2: Swap athletes to match their innskytter's lanes
      // Build a map of athlete UID to their target lane
      const athleteToTargetLane = new Map<number, number>();
      innskyttereData.forEach(innskytter => {
        // Check both innskytter and innskytter2 fields
        const innskytterLane = skiveNumbers.find(lane => 
          skiveData[lane]?.innskytter === innskytter.name || 
          skiveData[lane]?.innskytter2 === innskytter.name
        );
        if (innskytterLane && innskytter.athletes) {
          innskytter.athletes.forEach(athleteInfo => {
            athleteToTargetLane.set(athleteInfo.uid, innskytterLane);
          });
        }
      });

      // Group assignments by time slot
      const assignmentsByTime = new Map<string, { key: string; participant: Participant }[]>();
      Object.keys(newAssignments).forEach(key => {
        const participant = newAssignments[key];
        if (participant) {
          const [time] = key.split('-');
          if (!assignmentsByTime.has(time)) {
            assignmentsByTime.set(time, []);
          }
          assignmentsByTime.get(time)!.push({ key, participant });
        }
      });

      // For each time slot, rearrange athletes to match their target lanes
      assignmentsByTime.forEach((assignments, time) => {
        // Clear current assignments for this time slot
        assignments.forEach(({ key }) => {
          newAssignments[key] = null;
        });

        // Group by target lane
        const byTargetLane = new Map<number, Participant[]>();
        const noTargetLane: Participant[] = [];
        
        assignments.forEach(({ participant }) => {
          const targetLane = athleteToTargetLane.get(participant.Utover.UID);
          if (targetLane) {
            if (!byTargetLane.has(targetLane)) {
              byTargetLane.set(targetLane, []);
            }
            byTargetLane.get(targetLane)!.push(participant);
          } else {
            noTargetLane.push(participant);
          }
        });

        // Assign athletes with target lanes first (skip blocked cells)
        byTargetLane.forEach((participants, lane) => {
          const key1 = `${time}-${lane}-1`;
          const key2 = `${time}-${lane}-2`;
          
          if (!blockedCells[key1] && participants.length > 0) newAssignments[key1] = participants[0];
          if (!blockedCells[key2] && participants.length > 1) newAssignments[key2] = participants[1];
        });

        // Fill remaining empty slots with athletes without target lanes (skip blocked cells)
        let noTargetIndex = 0;
        for (const lane of skiveNumbers) {
          const key1 = `${time}-${lane}-1`;
          const key2 = `${time}-${lane}-2`;
          
          if (!blockedCells[key1] && !newAssignments[key1] && noTargetIndex < noTargetLane.length) {
            newAssignments[key1] = noTargetLane[noTargetIndex++];
          }
          if (!blockedCells[key2] && !newAssignments[key2] && noTargetIndex < noTargetLane.length) {
            newAssignments[key2] = noTargetLane[noTargetIndex++];
          }
        }
      });

      return newAssignments;
    });
  }, [getFilteredParticipants, getStartTime, timeSlots, skiveNumbers, innskyttereData, skiveData, blockedCells]);


  const chunkSkiveNumbers = useCallback((numbers: number[]): number[][] => {
    if (numbers.length <= 5) {
      return [numbers];
    }
    
    const chunks: number[][] = [];
    let remaining = [...numbers];
    
    while (remaining.length > 0) {
      if (remaining.length <= 5) {
        chunks.push(remaining);
        break;
      } else if (remaining.length === 6) {
        chunks.push(remaining.slice(0, 3));
        remaining = remaining.slice(3);
      } else if (remaining.length === 7) {
        chunks.push(remaining.slice(0, 4));
        remaining = remaining.slice(4);
      } else if (remaining.length === 8) {
        chunks.push(remaining.slice(0, 4));
        remaining = remaining.slice(4);
      } else if (remaining.length === 9) {
        chunks.push(remaining.slice(0, 5));
        remaining = remaining.slice(5);
      } else {
        chunks.push(remaining.slice(0, 5));
        remaining = remaining.slice(5);
      }
    }
    
    return chunks;
  }, []);

  const renderPrintHeader = useCallback(() => {
    return (
      <div className="print-header" style={{ display: 'none' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0', fontSize: '10px' }}>
          <tbody>
            <tr>
              <td colSpan={2} style={{ border: '1px solid black', padding: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                {getHeaderData().eventName}
              </td>
              <td colSpan={2} style={{ border: '1px solid black', padding: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                {getHeaderData().eventLocation}
              </td>
              <td rowSpan={3} style={{ backgroundColor: '#ff3333', border: '1px solid black', padding: '8px', textAlign: 'center', fontWeight: 'bold', color: 'white', verticalAlign: 'middle', width: '25%' }}>
                Husk vær klar 15 min før din innskyting starter
              </td>
            </tr>
            <tr>
              <td style={{ border: '1px solid black', padding: '2px', textAlign: 'left', fontSize: '9px' }}>
                Startnr/Ammo:
              </td>
              <td style={{ border: '1px solid black', padding: '2px', fontWeight: 'bold' }}>
                {getHeaderData().utdelingStartnummerAmmo}
              </td>
              <td style={{ border: '1px solid black', padding: '2px', textAlign: 'left', fontSize: '9px' }}>
                Antall min pr uttøver:
              </td>
              <td style={{ border: '1px solid black', padding: '2px', fontWeight: 'bold', color: getHeaderData().timePerAthleteMinutes < 10 ? '#ff0000' : '#000000' }}>
                {getHeaderData().timePerAthleteFormatted}
              </td>
            </tr>
            <tr>
              <td style={{ border: '1px solid black', padding: '2px', textAlign: 'left', fontSize: '9px' }}>
                Innpisker:
              </td>
              <td style={{ border: '1px solid black', padding: '2px', fontWeight: 'bold' }}>
                {getHeaderData().innpiskerTilInnskyting}
              </td>
              <td style={{ border: '1px solid black', padding: '2px', textAlign: 'left', fontSize: '9px' }}>
                Antall skiver
              </td>
              <td style={{ border: '1px solid black', padding: '2px', fontWeight: 'bold' }}>
                {getHeaderData().antallSkiver}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }, [getHeaderData]);

  const renderDragDropTable = () => {
    console.log('Rendering table with skiveNumbers:', skiveNumbers, 'timeSlots:', timeSlots);
    console.log('Current forsteSkive:', forsteSkive, 'sisteSkive:', sisteSkive);
    
    const skiveChunks = chunkSkiveNumbers(skiveNumbers);
    console.log('Split into chunks:', skiveChunks);
    
    return (
      <>
        {skiveChunks.map((chunkSkiveNumbers, chunkIndex) => (
          <div 
            key={`page-${chunkIndex}`}
            style={{ 
              pageBreakAfter: chunkIndex < skiveChunks.length - 1 ? 'always' : 'auto'
            }}
            className="innskyting-page"
          >
            {renderPrintHeader()}
            
            <Card style={{ marginBottom: '20px' }}>
              {chunkIndex === 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Title level={4} style={{ margin: 0 }}>Innskytingstabell</Title>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button 
                      type="default" 
                      size="small"
                      onClick={handleEmptyTable}
                      style={{ fontSize: '12px' }}
                    >
                      Empty Table
                    </Button>
                    <Button 
                      type="primary" 
                      size="small"
                      onClick={handleAddAllFromList}
                      style={{ fontSize: '12px' }}
                    >
                      Add All from List
                    </Button>
                  </div>
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table 
                  className="innskytingsliste-table"
                  style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    border: '2px solid #000',
                    fontSize: '14px'
                  }}
                  onDragOver={(e) => e.stopPropagation()}
                  onDrop={(e) => e.stopPropagation()}
                >
                  <thead>
                    <tr>
                      <th style={{ 
                        border: '1px solid #000', 
                        padding: '8px', 
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        minWidth: '80px'
                      }}>
                        
                      </th>
                      {chunkSkiveNumbers.map((skive: number) => {
                        console.log('Rendering header for skive:', skive);
                        return (
                          <th key={`header-${skive}`} className="skive-column" style={{ 
                            border: '1px solid #000', 
                            padding: '8px', 
                            backgroundColor: '#f5f5f5',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            minWidth: '120px'
                          }}>
                            Skive {skive}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ 
                        border: '1px solid #000', 
                        padding: '8px', 
                        backgroundColor: '#f9f9f9',
                        fontWeight: 'bold'
                      }}>
                        Innskytter
                      </td>
                      {chunkSkiveNumbers.map(skive => {
                        const cellKey = `innskytter-${skive}`;
                        const innskytterName = skiveData[skive]?.innskytter;
                        const innskytterName2 = skiveData[skive]?.innskytter2;
                        return (
                          <td 
                            key={cellKey}
                            style={{ 
                              border: '1px solid #000', 
                              padding: '8px',
                              backgroundColor: '#fff',
                              minHeight: '40px',
                              textAlign: 'center'
                            }}
                          >
                            {innskytterName && (
                              <div style={{
                                padding: '4px',
                                fontSize: '12px',
                                color: '#000'
                              }}>
                                {innskytterName}
                              </div>
                            )}
                            {antallInnskyttere === 2 && innskytterName2 && (
                              <div style={{
                                padding: '4px',
                                fontSize: '12px',
                                color: '#000',
                                borderTop: innskytterName ? '1px solid #e8e8e8' : 'none',
                                marginTop: innskytterName ? '4px' : '0'
                              }}>
                                {innskytterName2}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td style={{ 
                        border: '1px solid #000', 
                        padding: '8px', 
                        backgroundColor: '#f9f9f9',
                        fontWeight: 'bold'
                      }}>
                        Medhjelper
                      </td>
                      {chunkSkiveNumbers.map(skive => {
                        const cellKey = `medhjelper-${skive}`;
                        const medhjelperName = skiveData[skive]?.medhjelper;
                        return (
                          <td 
                            key={cellKey}
                            style={{ 
                              border: '1px solid #000', 
                              padding: '8px',
                              backgroundColor: '#fff',
                              minHeight: '40px',
                              textAlign: 'center'
                            }}
                          >
                            {medhjelperName && (
                              <div style={{
                                padding: '4px',
                                fontSize: '12px',
                                color: '#000'
                              }}>
                                {medhjelperName}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {timeSlots.map((time: string, timeIndex: number) => {
                      console.log(`Rendering time slot ${time} with chunkSkiveNumbers:`, chunkSkiveNumbers);
                      
                      const nextTimeSlot = timeSlots[timeIndex + 1];
                      const pappbytteInfo = getPappbytteInfo(time, nextTimeSlot);
                      const isPappbytteStart = pappbytteInfo.isPappbytte;
                      const extendedEndTime = pappbytteInfo.extendedEndTime;
                      const pappbytteStart = pappbytteInfo.pappbytteStart;
                      const pappbytteEnd = pappbytteInfo.pappbytteEnd;
                      
                      // Display the time range with extended end time if pappbytte
                      const displayTime = isPappbytteStart && extendedEndTime 
                        ? `${time} - ${extendedEndTime}` 
                        : time;
                      
                      return (
                        <tr key={`time-${time}-chunk-${chunkIndex}`}>
                          <td 
                            className="time-column" 
                            style={{ 
                              border: '1px solid #000', 
                              padding: '8px', 
                              backgroundColor: isPappbytteStart ? '#fff3cd' : '#f9f9f9',
                              fontWeight: 'bold',
                              verticalAlign: 'top'
                            }}
                          >
                            {displayTime}
                            {isPappbytteStart && pappbytteStart && pappbytteEnd && (
                              <div style={{ fontSize: '13px', color: '#856404', marginTop: '4px', fontWeight: 'normal' }}>
                                Pappbytte {pappbytteStart}-{pappbytteEnd}
                              </div>
                            )}
                          </td>
                          {chunkSkiveNumbers.map(skive => {
                            const cellKey1 = `${time}-${skive}-1`;
                            const cellKey2 = `${time}-${skive}-2`;
                            const participant1 = tableAssignments[cellKey1];
                            const participant2 = tableAssignments[cellKey2];
                            const isBlocked1 = blockedCells[cellKey1];
                            const isBlocked2 = blockedCells[cellKey2];
                            console.log(`Rendering cell ${cellKey1} and ${cellKey2} for skive ${skive}`);
                            return (
                              <td 
                                key={`${time}-${skive}`}
                                style={{ 
                                  border: '1px solid #000', 
                                  padding: '0px',
                                  backgroundColor: isPappbytteStart ? '#fff9e6' : 'transparent'
                                }}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '80px' }}>
                                  <div 
                                    style={{ 
                                      flex: 1, 
                                      borderBottom: '1px solid #ccc',
                                      padding: '2px',
                                      backgroundColor: isBlocked1 ? '#d9d9d9' : (participant1 ? '#e6f7ff' : '#fff'),
                                      cursor: isBlocked1 ? 'not-allowed' : (participant1 ? 'move' : 'default'),
                                      minHeight: '38px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onDragOver={isBlocked1 ? undefined : handleDragOver}
                                    onDrop={isBlocked1 ? undefined : (e) => handleDrop(e, cellKey1)}
                                    onDoubleClick={() => {
                                      if (isBlocked1) {
                                        setBlockedCells(prev => {
                                          const updated = {...prev};
                                          delete updated[cellKey1];
                                          return updated;
                                        });
                                      } else if (participant1) {
                                        setTableAssignments(prev => ({
                                          ...prev,
                                          [cellKey1]: null
                                        }));
                                      } else {
                                        setSelectedCellForBlock(cellKey1);
                                        setBlockCellReason('');
                                        setBlockCellModalVisible(true);
                                      }
                                    }}
                                  >
                                    {isBlocked1 ? (
                                      <div style={{
                                        fontSize: '10px',
                                        color: '#666',
                                        textAlign: 'center',
                                        fontStyle: 'italic',
                                        padding: '2px'
                                      }}>
                                        {isBlocked1}
                                      </div>
                                    ) : participant1 && (
                                      <div
                                        className="participant-name"
                                        draggable
                                        onDragStart={(e) => handleCellDragStart(e, participant1)}
                                        style={{
                                          padding: '2px 4px',
                                          backgroundColor: '#1890ff',
                                          color: 'white',
                                          borderRadius: '3px',
                                          fontSize: '11px',
                                          cursor: 'move',
                                          width: '100%',
                                          textAlign: 'center'
                                        }}
                                      >
                                        {participant1.Startnummer} - {participant1.Utover.Fornavn} {participant1.Utover.Etternavn}
                                      </div>
                                    )}
                                  </div>
                                  <div 
                                    style={{ 
                                      flex: 1, 
                                      padding: '2px',
                                      backgroundColor: isBlocked2 ? '#d9d9d9' : (participant2 ? '#e6f7ff' : '#fff'),
                                      cursor: isBlocked2 ? 'not-allowed' : (participant2 ? 'move' : 'default'),
                                      minHeight: '38px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onDragOver={isBlocked2 ? undefined : handleDragOver}
                                    onDrop={isBlocked2 ? undefined : (e) => handleDrop(e, cellKey2)}
                                    onDoubleClick={() => {
                                      if (isBlocked2) {
                                        setBlockedCells(prev => {
                                          const updated = {...prev};
                                          delete updated[cellKey2];
                                          return updated;
                                        });
                                      } else if (participant2) {
                                        setTableAssignments(prev => ({
                                          ...prev,
                                          [cellKey2]: null
                                        }));
                                      } else {
                                        setSelectedCellForBlock(cellKey2);
                                        setBlockCellReason('');
                                        setBlockCellModalVisible(true);
                                      }
                                    }}
                                  >
                                    {isBlocked2 ? (
                                      <div style={{
                                        fontSize: '10px',
                                        color: '#666',
                                        textAlign: 'center',
                                        fontStyle: 'italic',
                                        padding: '2px'
                                      }}>
                                        {isBlocked2}
                                      </div>
                                    ) : participant2 && (
                                      <div
                                        className="participant-name"
                                        draggable
                                        onDragStart={(e) => handleCellDragStart(e, participant2)}
                                        style={{
                                          padding: '2px 4px',
                                          backgroundColor: '#1890ff',
                                          color: 'white',
                                          borderRadius: '3px',
                                          fontSize: '11px',
                                          cursor: 'move',
                                          width: '100%',
                                          textAlign: 'center'
                                        }}
                                      >
                                        {participant2.Startnummer} - {participant2.Utover.Fornavn} {participant2.Utover.Etternavn}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    {sisteInnskyting && chunkIndex === skiveChunks.length - 1 && (
                      <tr>
                        <td 
                          colSpan={chunkSkiveNumbers.length + 1}
                          style={{ 
                            border: '1px solid #000', 
                            padding: '8px', 
                            backgroundColor: '#f9f9f9',
                            fontWeight: 'bold',
                            textAlign: 'center'
                          }}
                        >
                          Slutt på Innskyting: {sisteInnskyting.format('HH:mm')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        ))}
      </>
    );
  };


  const columns = [
    {
      title: 'Start Nr',
      dataIndex: 'Startnummer',
      key: 'startnummer',
      width: 80,
      sorter: (a: Participant, b: Participant) => a.Startnummer - b.Startnummer,
    },
    {
      title: 'Name',
      key: 'name',
      render: (participant: Participant) => (
        <div
          draggable
          onDragStart={(e) => {
            setDraggedParticipant(participant);
            console.log('Drag started for participant:', participant.Utover.Fornavn);
            if (e.dataTransfer) {
              e.dataTransfer.effectAllowed = 'move';
            }
          }}
          style={{
            cursor: isParticipantAssigned(participant) ? 'default' : 'move',
            backgroundColor: isParticipantAssigned(participant) ? '#f6ffed' : 'transparent',
            padding: '4px',
            borderRadius: '4px'
          }}
        >
          {`${participant.Utover.Fornavn} ${participant.Utover.Etternavn}`}
        </div>
      ),
      sorter: (a: Participant, b: Participant) => 
        `${a.Utover.Fornavn} ${a.Utover.Etternavn}`.localeCompare(`${b.Utover.Fornavn} ${b.Utover.Etternavn}`),
    },
    {
      title: 'Club',
      dataIndex: ['Utover', 'Klubb', 'Navn'],
      key: 'club',
      sorter: (a: Participant, b: Participant) => a.Utover.Klubb.Navn.localeCompare(b.Utover.Klubb.Navn),
    },
    {
      title: 'Team',
      key: 'team',
      render: (participant: Participant) => participant.Utover.Team?.Navn || '-',
      sorter: (a: Participant, b: Participant) => 
        (a.Utover.Team?.Navn || '').localeCompare(b.Utover.Team?.Navn || ''),
    },
    {
      title: 'Class',
      dataIndex: ['Klasse', 'Navn'],
      key: 'class',
      sorter: (a: Participant, b: Participant) => a.Klasse.Navn.localeCompare(b.Klasse.Navn),
    },
    {
      title: 'Start Time',
      key: 'startTime',
      render: (participant: Participant) => getStartTime(participant),
      sorter: (a: Participant, b: Participant) => getStartTime(a).localeCompare(getStartTime(b)),
    },
    {
      title: 'Status',
      key: 'status',
      render: (participant: Participant) => {
        const assignment = getParticipantAssignment(participant);
        const isAssigned = isParticipantAssigned(participant);
        
        if (isAssigned && assignment) {
          return (
            <span 
              style={{ 
                color: '#52c41a',
                fontWeight: 'bold',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={() => {
                const confirmed = window.confirm(`Remove ${participant.Utover.Fornavn} ${participant.Utover.Etternavn} from Lane ${assignment.lane}, ${assignment.time}?`);
                if (confirmed) {
                  handleRemoveParticipant(participant);
                }
              }}
            >
              Lane {assignment.lane}, {assignment.time}
            </span>
          );
        }
        
        return (
          <Select
            placeholder="Select lane"
            style={{ width: 120 }}
            size="small"
            onChange={(selectedLane) => handleAddToLane(participant, selectedLane)}
            disabled={skiveNumbers.length === 0 || skiveNumbers.every(lane => isLaneFull(lane))}
          >
            {skiveNumbers.map(lane => {
              const innskytterName = skiveData[lane]?.innskytter;
              const innskytterName2 = skiveData[lane]?.innskytter2;
              let laneLabel = `Lane ${lane}`;
              if (innskytterName && innskytterName2) {
                laneLabel = `Lane ${lane} (${innskytterName}, ${innskytterName2})`;
              } else if (innskytterName) {
                laneLabel = `Lane ${lane} (${innskytterName})`;
              }
              
              return (
                <Option 
                  key={lane} 
                  value={lane}
                  disabled={isLaneFull(lane)}
                >
                  {laneLabel}
                </Option>
              );
            })}
          </Select>
        );
      },
      filters: [
        { text: 'Assigned', value: 'assigned' },
        { text: 'Available', value: 'available' }
      ],
      onFilter: (value: boolean | React.Key, record: Participant) => {
        const assigned = isParticipantAssigned(record);
        return value === 'assigned' ? assigned : !assigned;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (participant: Participant) => (
        <Button
          size="small"
          type="text"
          danger
          onClick={() => {
            const bibNumber = participant.Startnummer.toString();
            const currentBibs = excludeBibs ? excludeBibs.split(';').map(b => b.trim()).filter(b => b) : [];
            
            if (!currentBibs.includes(bibNumber)) {
              const newBibs = [...currentBibs, bibNumber];
              // Sort numerically
              const sortedBibs = newBibs.sort((a, b) => parseInt(a) - parseInt(b));
              setExcludeBibs(sortedBibs.join(';'));
              message.success(`Bib ${bibNumber} hidden`);
            }
          }}
        >
          Hide
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert message="No Event Selected" description="Please select an event from the home page." type="warning" showIcon />
      </div>
    );
  }

  return (
    <div 
      style={{ padding: '20px' }}
      onDragOver={handleDragOverOutside}
      onDrop={handleDropOutsideTable}
    >
      <header style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Title level={2} style={{ margin: 0 }}>Innskytingsliste</Title>
          <div style={{ textAlign: 'right' }}>
            <Text strong>Event: {selectedEvent?.Name || 'No event selected'}</Text>
            <br />
            <Text type="secondary">
              {selectedEvent?.City?.Name || 'Unknown'} • {new Date(selectedEvent?.Date || Date.now()).toLocaleDateString('no-NO')} • {selectedEvent?.Organizer?.Name || 'Unknown'}
            </Text>
          </div>
        </div>

        <Tabs
          activeKey={puljename || (availableGroups.length > 0 ? availableGroups[0] : 'ny-pulje')}
          onChange={async (key) => {
            if (key === 'ny-pulje') {
              // Auto-create new group with name "Pulje {count}"
              const newGroupNumber = availableGroups.length + 1;
              const newGroupName = `Pulje ${newGroupNumber}`;
              
              try {
                if (!selectedEvent || !user || !selectedProfileId) {
                  message.error('Missing required data');
                  return;
                }
                
                const profileRef = doc(db, 'profiles', selectedProfileId);
                const profileDoc = await getDoc(profileRef);
                
                if (!profileDoc.exists()) {
                  message.error('Profile not found');
                  return;
                }
                
                const profileData = profileDoc.data();
                const eventSetups = profileData.setupData?.[selectedEvent.Id] || {};
                
                // Create new empty group data
                const newGroupData = {
                  setup: {
                    startInnskyting: null,
                    sisteInnskyting: null,
                    forsteSkive: 1,
                    sisteSkive: 5,
                    skiveData: {},
                    tidPerPar: '10',
                    showSkiveDetails: false,
                    utdelingStartnummerAmmo: '',
                    innpiskerTilInnskyting: '',
                    pappBytteFra: null,
                    pappBytteTil: null,
                    puljename: newGroupName,
                    blockedCells: {},
                    antallAndreUttovere: 0,
                    antallInnskyttere: 1
                  },
                  filters: {},
                  tableAssignments: {}
                };
                
                const updatedSetupData = {
                  ...profileData.setupData,
                  [selectedEvent.Id]: {
                    ...eventSetups,
                    [newGroupName]: newGroupData
                  }
                };
                
                await setDoc(profileRef, {
                  ...profileData,
                  setupData: updatedSetupData,
                  updatedAt: new Date().toISOString()
                });
                
                message.success(`Group "${newGroupName}" created successfully`);
                setPuljename(newGroupName);
                setAvailableGroups([...availableGroups, newGroupName].sort());
              } catch (error) {
                console.error('Error creating new group:', error);
                message.error('Failed to create group');
              }
            } else {
              setPuljename(key);
            }
          }}
          items={[
            // Create a tab for each available group
            ...availableGroups.map(groupName => ({
              key: groupName,
              label: groupName,
              children: (
                <>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                    <Button 
                      icon={<FilterOutlined />} 
                      onClick={() => setFilterModalVisible(true)}
                    >
                      Filter
                    </Button>
                    <Button 
                      icon={<SettingOutlined />} 
                      onClick={() => {
                        setTempStartInnskyting(startInnskyting);
                        setTempSisteInnskyting(sisteInnskyting);
                        setTempForsteSkive(forsteSkive);
                        setTempSisteSkive(sisteSkive);
                        setTempSkiveData(skiveData);
                        setTempTidPerPar(tidPerPar);
                        setTempShowSkiveDetails(showSkiveDetails);
                        setTempUtdelingStartnummerAmmo(utdelingStartnummerAmmo);
                        setTempInnpiskerTilInnskyting(innpiskerTilInnskyting);
                        setTempPappBytteFra(pappBytteFra);
                        setTempPappBytteTil(pappBytteTil);
                        setTempAntallAndreUttovere(antallAndreUttovere);
                        setTempAntallInnskyttere(antallInnskyttere);
                        setTempPuljename(puljename);
                        setSettingsModalVisible(true);
                      }}
                    >
                      Setup
                    </Button>
                    <Button 
                      icon={<DownloadOutlined />} 
                      onClick={handlePrint}
                      type="primary"
                    >
                      Download PDF
                    </Button>
                    <Button 
                      icon={<DownloadOutlined />} 
                      onClick={handleDownloadInnpiskerliste}
                      type="default"
                    >
                      Download Innpiskerliste
                    </Button>
                    <Button 
                      icon={<DownloadOutlined />} 
                      onClick={handleDownloadBothReports}
                      type="primary"
                      style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    >
                      Download Both Reports
                    </Button>
                    {availableGroups.length > 1 && (
                      <Button 
                        icon={<DeleteOutlined />} 
                        onClick={() => {
                          setGroupToDelete(groupName);
                          setDeleteModalVisible(true);
                        }}
                        danger
                      >
                        Delete Group
                      </Button>
                    )}
                  </div>

                  {selectedEvent && (
                    <>
                      {renderDragDropTable()}
                      
                      <div style={{ marginBottom: '20px' }}>
                        <Text strong>Filtered Participants: {filteredParticipants.length}</Text>
                        {(selectedClubs.length > 0 || selectedTeams.length > 0 || selectedClasses.length > 0 || nameFilter || startTimeFrom || startTimeTo) && (
                          <div style={{ marginTop: '8px' }}>
                            <Text type="secondary">
                              Active filters: 
                              {selectedClubs.length > 0 && ` Clubs (${selectedClubs.length})`}
                              {selectedTeams.length > 0 && ` Teams (${selectedTeams.length})`}
                              {selectedClasses.length > 0 && ` Classes (${selectedClasses.length})`}
                              {nameFilter && ` Name: "${nameFilter}"`}
                              {startTimeFrom && ` Start from: ${startTimeFrom}`}
                              {startTimeTo && ` Start to: ${startTimeTo}`}
                            </Text>
                          </div>
                        )}

                        {filteredParticipants.length > 0 && (
                          <Table
                            dataSource={filteredParticipants}
                            columns={columns}
                            rowKey="Startnummer"
                            pagination={{
                              pageSize: 50,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} participants`,
                            }}
                            size="small"
                            scroll={{ x: 800 }}
                          />
                        )}
                      </div>
                    </>
                  )}
                </>
              )
            })),
            // Add the "Ny Pulje**" tab at the end
            {
              key: 'ny-pulje',
              label: 'Ny Pulje**',
              children: (
                <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto' }}>
                  <Title level={3}>Create New Group</Title>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                    Create a new group with independent setup, filters, and athlete assignments for this event.
                  </Text>
                  <Form
                    layout="vertical"
                    onFinish={async (values) => {
                      const newGroupName = values.groupName.trim();
                      if (!newGroupName) {
                        message.error('Group name cannot be empty');
                        return;
                      }
                      
                      if (availableGroups.includes(newGroupName)) {
                        message.error('A group with this name already exists');
                        return;
                      }
                      
                      try {
                        // Create a new empty group
                        if (!selectedEvent || !user || !selectedProfileId) {
                          message.error('Missing required data');
                          return;
                        }
                        
                        const profileRef = doc(db, 'profiles', selectedProfileId);
                        const profileDoc = await getDoc(profileRef);
                        
                        if (!profileDoc.exists()) {
                          message.error('Profile not found');
                          return;
                        }
                        
                        const profileData = profileDoc.data();
                        const eventSetups = profileData.setupData?.[selectedEvent.Id] || {};
                        
                        // Create new empty group data
                        const newGroupData = {
                          setup: {
                            startInnskyting: null,
                            sisteInnskyting: null,
                            forsteSkive: 1,
                            sisteSkive: 5,
                            skiveData: {},
                            tidPerPar: '10',
                            showSkiveDetails: false,
                            utdelingStartnummerAmmo: '',
                            innpiskerTilInnskyting: '',
                            pappBytteFra: null,
                            pappBytteTil: null,
                            puljename: newGroupName,
                            blockedCells: {},
                            antallAndreUttovere: 0,
                            antallInnskyttere: 1
                          },
                          filters: {},
                          tableAssignments: {}
                        };
                        
                        const updatedSetupData = {
                          ...profileData.setupData,
                          [selectedEvent.Id]: {
                            ...eventSetups,
                            [newGroupName]: newGroupData
                          }
                        };
                        
                        await setDoc(profileRef, {
                          ...profileData,
                          setupData: updatedSetupData,
                          updatedAt: new Date().toISOString()
                        });
                        
                        message.success(`Group "${newGroupName}" created successfully`);
                        setPuljename(newGroupName);
                        setAvailableGroups([...availableGroups, newGroupName].sort());
                        
                        // Reset form
                        values.groupName = '';
                      } catch (error) {
                        console.error('Error creating new group:', error);
                        message.error('Failed to create group');
                      }
                    }}
                  >
                    <Form.Item
                      label="Group Name"
                      name="groupName"
                      rules={[{ required: true, message: 'Please enter a group name' }]}
                    >
                      <Input placeholder="e.g., Morning Session, Afternoon Session, Juniors" size="large" />
                    </Form.Item>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" size="large" block>
                        Create Group
                      </Button>
                    </Form.Item>
                  </Form>
                  
                  {availableGroups.length > 0 && (
                    <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #f0f0f0' }}>
                      <Title level={4}>Existing Groups</Title>
                      <ul style={{ marginTop: '16px' }}>
                        {availableGroups.map(group => (
                          <li key={group} style={{ marginBottom: '8px' }}>
                            <Text strong={group === puljename}>{group}</Text>
                            {group === puljename && <Text type="secondary"> (active)</Text>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )
            }
          ]}
        />
      </header>

      {selectedEvent && (
        <>
          <Modal
            title="Delete Group"
            open={deleteModalVisible}
            onOk={async () => {
              if (!selectedEvent || !user || !selectedProfileId) {
                message.error('Missing required data');
                setDeleteModalVisible(false);
                return;
              }
              
              try {
                const profileRef = doc(db, 'profiles', selectedProfileId);
                const profileDoc = await getDoc(profileRef);
                
                if (!profileDoc.exists()) {
                  message.error('Profile not found');
                  setDeleteModalVisible(false);
                  return;
                }
                
                const profileData = profileDoc.data();
                const eventSetups = profileData.setupData?.[selectedEvent.Id] || {};
                
                // Remove the group
                const { [groupToDelete]: removed, ...remainingSetups } = eventSetups;
                
                // Update Firebase
                const updatedSetupData = {
                  ...profileData.setupData,
                  [selectedEvent.Id]: remainingSetups
                };
                
                await setDoc(profileRef, {
                  ...profileData,
                  setupData: updatedSetupData,
                  updatedAt: new Date().toISOString()
                });
                
                // Update local state
                const updatedGroups = availableGroups.filter(g => g !== groupToDelete).sort();
                setAvailableGroups(updatedGroups);
                
                // Switch to the first remaining group
                if (updatedGroups.length > 0) {
                  setPuljename(updatedGroups[0]);
                }
                
                message.success(`Group "${groupToDelete}" deleted successfully`);
                setDeleteModalVisible(false);
                setGroupToDelete('');
              } catch (error) {
                console.error('Error deleting group:', error);
                message.error('Failed to delete group');
                setDeleteModalVisible(false);
              }
            }}
            onCancel={() => {
              setDeleteModalVisible(false);
              setGroupToDelete('');
            }}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <p>Are you sure you want to delete "{groupToDelete}"? This action cannot be undone.</p>
          </Modal>

          <Modal
            title="Filter Participants"
            open={filterModalVisible}
            onOk={handleFilterOk}
            onCancel={handleFilterCancel}
            width={600}
            footer={[
              <Button key="reset" onClick={handleFilterReset}>
                Reset Filters
              </Button>,
              <Button key="cancel" onClick={handleFilterCancel}>
                Cancel
              </Button>,
              <Button key="ok" type="primary" onClick={handleFilterOk}>
              Apply Filters
            </Button>,
          ]}
        >
          <Form layout="vertical">
            <Form.Item label="Search by Name">
              <Input 
                placeholder="Enter participant name" 
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
            </Form.Item>
            <Form.Item 
              label="Exclude Bibs (semicolon separated)"
              extra={excludeBibs && `Excluding: ${excludeBibs.split(';').map(b => b.trim()).filter(b => b).sort((a, b) => parseInt(a) - parseInt(b)).join(', ')}`}
            >
              <Input 
                placeholder="e.g., 5;12;23" 
                value={excludeBibs}
                onChange={(e) => setExcludeBibs(e.target.value)}
                onBlur={(e) => {
                  // Sort the bibs when user finishes typing
                  const value = e.target.value;
                  const bibs = value.split(';').map(b => b.trim()).filter(b => b);
                  const sortedBibs = bibs.sort((a, b) => {
                    const numA = parseInt(a);
                    const numB = parseInt(b);
                    if (isNaN(numA)) return 1;
                    if (isNaN(numB)) return -1;
                    return numA - numB;
                  });
                  setExcludeBibs(sortedBibs.join(';'));
                }}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Filter by Clubs">
                  <Select
                    mode="multiple"
                    placeholder="Select clubs"
                    allowClear
                    style={{ width: '100%' }}
                    value={selectedClubs}
                    onChange={setSelectedClubs}
                  >
                    {clubs.map(club => (
                      <Select.Option key={club} value={club}>
                        {club}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Filter by Teams">
                  <Select
                    mode="multiple"
                    placeholder="Select teams"
                    allowClear
                    style={{ width: '100%' }}
                    value={selectedTeams}
                    onChange={setSelectedTeams}
                  >
                    {teams.map(team => (
                      <Select.Option key={team} value={team}>
                        {team}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Filter by Krets">
                  <Select
                    mode="multiple"
                    placeholder="Select krets"
                    allowClear
                    style={{ width: '100%' }}
                    value={selectedKretser}
                    onChange={setSelectedKretser}
                  >
                    {kretser.map(krets => (
                      <Select.Option key={krets} value={krets}>
                        {krets}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Filter by Classes">
                  <Select
                    mode="multiple"
                    placeholder="Select classes"
                    allowClear
                    style={{ width: '100%' }}
                    value={selectedClasses}
                    onChange={setSelectedClasses}
                  >
                    {classes.map(classItem => (
                      <Select.Option key={classItem} value={classItem}>
                        {classItem}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Start Time From">
              <TimePicker
                value={startTimeFrom ? dayjs(startTimeFrom, 'HH:mm') : null}
                onChange={(time) => setStartTimeFrom(time ? time.format('HH:mm') : '')}
                format="HH:mm"
                placeholder="Select start time"
                allowClear
              />
            </Form.Item>
            <Form.Item label="Start Time To">
              <TimePicker
                value={startTimeTo ? dayjs(startTimeTo, 'HH:mm') : null}
                onChange={(time) => setStartTimeTo(time ? time.format('HH:mm') : '')}
                format="HH:mm"
                placeholder="Select end time"
                allowClear
              />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Setup"
          open={settingsModalVisible}
          onOk={handleSettingsOk}
          onCancel={handleSettingsCancel}
          width={800}
          footer={[
            <Button key="cancel" onClick={handleSettingsCancel}>
              Cancel
            </Button>,
            <Button key="ok" type="primary" onClick={handleSettingsOk}>
              OK
            </Button>
          ]}
          styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        >
          <Form layout="vertical" style={{ padding: '20px' }}>
            <Form.Item label="Pulje Name">
              <Input
                value={tempPuljename}
                onChange={(e) => setTempPuljename(e.target.value)}
                placeholder="Enter pulje name"
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Row gutter={16} align="bottom">
              <Col span={8}>
                <Form.Item label="Start innskyting">
                  <TimePicker
                    value={tempStartInnskyting}
                    onChange={setTempStartInnskyting}
                    format="HH:mm"
                    placeholder="Velg tid"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Slutt innskyting">
                  <TimePicker
                    value={tempSisteInnskyting}
                    onChange={setTempSisteInnskyting}
                    format="HH:mm"
                    placeholder="Velg tid"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label=" " colon={false}>
                  <Button 
                    type="link" 
                    icon={tempShowAdvanced ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => setTempShowAdvanced(!tempShowAdvanced)}
                    style={{ padding: '4px 0' }}
                  >
                    {tempShowAdvanced ? 'Hide' : 'Show'} advanced
                  </Button>
                </Form.Item>
              </Col>
            </Row>
            
            {tempShowAdvanced && (
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="Pappbytte fra">
                    <TimePicker
                      value={tempPappBytteFra}
                      onChange={setTempPappBytteFra}
                      format="HH:mm"
                      placeholder="Velg tid"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Pappbytte til">
                    <TimePicker
                      value={tempPappBytteTil}
                      onChange={setTempPappBytteTil}
                      format="HH:mm"
                      placeholder="Velg tid"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Antall andre">
                    <InputNumber
                      min={0}
                      max={1000}
                      value={tempAntallAndreUttovere}
                      onChange={(value) => setTempAntallAndreUttovere(value || 0)}
                      style={{ width: '100%' }}
                      placeholder="0"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Ant. innskyttere">
                    <InputNumber
                      min={1}
                      max={2}
                      value={tempAntallInnskyttere}
                      onChange={(value) => setTempAntallInnskyttere(value || 1)}
                      style={{ width: '100%' }}
                      placeholder="1"
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Første skive">
                  <InputNumber
                    min={0}
                    max={35}
                    value={tempForsteSkive}
                    onChange={(value) => setTempForsteSkive(value || 1)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Siste skive">
                  <InputNumber
                    min={tempForsteSkive}
                    max={35}
                    value={tempSisteSkive}
                    onChange={(value) => setTempSisteSkive(value || 1)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Tid per par" style={{ marginBottom: '16px' }}>
              <Row gutter={8}>
                <Col flex="auto">
                  <Input
                    value={tempTidPerPar}
                    onChange={(e) => setTempTidPerPar(e.target.value)}
                    placeholder="Beregnes automatisk"
                    suffix="min"
                  />
                </Col>
                <Col>
                  <Tooltip title={getCalculateButtonState().disabled ? getCalculateButtonState().tooltip : ''}>
                    <Button 
                      onClick={handleCalculateTimePerPair}
                      type="default"
                      htmlType="button"
                      disabled={getCalculateButtonState().disabled}
                    >
                      Calculate
                    </Button>
                  </Tooltip>
                </Col>
              </Row>
            </Form.Item>
            
            <div style={{ marginTop: '12px' }}>
              <Button 
                type="dashed" 
                icon={tempShowSkiveDetails ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setTempShowSkiveDetails(!tempShowSkiveDetails)}
                style={{ width: '100%', marginBottom: '12px' }}
                size="small"
              >
                {tempShowSkiveDetails ? 'Skjul' : 'Vis'} innskytter og medhjelper detaljer
              </Button>
              
              {tempShowSkiveDetails && (
                <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px', backgroundColor: '#fafafa' }}>
                  <div style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e8e8e8' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px', color: '#595959' }}>
                      Generelle innstillinger
                    </div>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item label="Utdeling av startnr/Ammo" style={{ marginBottom: '8px' }}>
                          <Input
                            value={tempUtdelingStartnummerAmmo}
                            onChange={(e) => setTempUtdelingStartnummerAmmo(e.target.value)}
                            placeholder="Startnr/Ammo"
                            style={{ width: '100%' }}
                            size="small"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Innpisker - til innskyting" style={{ marginBottom: '8px' }}>
                          <Input
                            value={tempInnpiskerTilInnskyting}
                            onChange={(e) => setTempInnpiskerTilInnskyting(e.target.value)}
                            placeholder="innpisker"
                            style={{ width: '100%' }}
                            size="small"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px', color: '#595959' }}>
                          Skive tildelinger
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: tempAntallInnskyttere === 2 ? 'auto 1fr 1fr 1fr' : 'auto 1fr 1fr', gap: '4px', alignItems: 'center' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>Skive</div>
                        <div style={{ fontWeight: 'bold', fontSize: '12px', textAlign: 'center' }}>Innskytter</div>
                        {tempAntallInnskyttere === 2 && (
                          <div style={{ fontWeight: 'bold', fontSize: '12px', textAlign: 'center' }}>Innskytter 2</div>
                        )}
                        <div style={{ fontWeight: 'bold', fontSize: '12px', textAlign: 'center' }}>Medhjelper</div>
                        {Array.from({ length: tempSisteSkive - tempForsteSkive + 1 }, (_, index) => {
                          const skiveNumber = tempForsteSkive + index;
                          return (
                            <React.Fragment key={skiveNumber}>
                              <div style={{ 
                                backgroundColor: '#000', 
                                color: '#fff', 
                                padding: '2px 6px', 
                                fontSize: '11px', 
                                fontWeight: 'bold',
                                textAlign: 'center',
                                borderRadius: '2px'
                              }}>
                                Skive {skiveNumber}
                              </div>
                              <Input
                                placeholder="<Innskytter>"
                                value={tempSkiveData[skiveNumber]?.innskytter || ''}
                                onChange={(e) => setTempSkiveData(prev => ({
                                  ...prev,
                                  [skiveNumber]: {
                                    ...prev[skiveNumber],
                                    innskytter: e.target.value,
                                    innskytter2: prev[skiveNumber]?.innskytter2 || '',
                                    medhjelper: prev[skiveNumber]?.medhjelper || ''
                                  }
                                }))}
                                size="small"
                                style={{ fontSize: '11px' }}
                              />
                              {tempAntallInnskyttere === 2 && (
                                <Input
                                  placeholder="<Innskytter 2>"
                                  value={tempSkiveData[skiveNumber]?.innskytter2 || ''}
                                  onChange={(e) => setTempSkiveData(prev => ({
                                    ...prev,
                                    [skiveNumber]: {
                                      ...prev[skiveNumber],
                                      innskytter2: e.target.value,
                                      medhjelper: prev[skiveNumber]?.medhjelper || ''
                                    }
                                  }))}
                                  size="small"
                                  style={{ fontSize: '11px' }}
                                />
                              )}
                              <Input
                                placeholder="<Medhjelper>"
                                value={tempSkiveData[skiveNumber]?.medhjelper || ''}
                                onChange={(e) => setTempSkiveData(prev => ({
                                  ...prev,
                                  [skiveNumber]: {
                                    ...prev[skiveNumber],
                                    medhjelper: e.target.value,
                                    innskytter: prev[skiveNumber]?.innskytter || '',
                                    innskytter2: prev[skiveNumber]?.innskytter2 || ''
                                  }
                                }))}
                                size="small"
                                style={{ fontSize: '11px' }}
                              />
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div style={{ flex: '0 0 300px', borderLeft: '1px solid #e8e8e8', paddingLeft: '12px' }}>
                      <InnskyttereList onAssignToLane={handleAssignInnskytterToLane} filteredParticipants={getFilteredParticipants()} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <Form.Item label="Import/Export Setup" style={{ marginTop: '24px' }}>
              <Row gutter={16}>
                <Col>
                  <Button 
                    icon={<DownloadOutlined />} 
                    onClick={handleExportSetup}
                    className="export-button"
                  >
                    Export Setup
                  </Button>
                </Col>
                <Col>
                  <Upload
                    accept=".json"
                    beforeUpload={handleImportSetup}
                    showUploadList={false}
                  >
                    <Button 
                      icon={<UploadOutlined />}
                      className="import-button"
                    >
                      Import Setup
                    </Button>
                  </Upload>
                </Col>
              </Row>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Block Cell"
          open={blockCellModalVisible}
          onOk={() => {
            if (selectedCellForBlock && blockCellReason.trim()) {
              setBlockedCells(prev => ({
                ...prev,
                [selectedCellForBlock]: blockCellReason.trim()
              }));
              setBlockCellModalVisible(false);
              setSelectedCellForBlock(null);
              setBlockCellReason('');
            } else {
              message.warning('Please enter a reason for blocking this cell');
            }
          }}
          onCancel={() => {
            setBlockCellModalVisible(false);
            setSelectedCellForBlock(null);
            setBlockCellReason('');
          }}
          okText="Block Cell"
          cancelText="Cancel"
        >
          <Form layout="vertical">
            <Form.Item label="Reason for blocking" required>
              <Input.TextArea
                value={blockCellReason}
                onChange={(e) => setBlockCellReason(e.target.value)}
                placeholder="Enter reason (e.g., Equipment maintenance, Reserved for officials, etc.)"
                rows={3}
                autoFocus
              />
            </Form.Item>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
              <strong>Tip:</strong> Double-click a blocked cell to unblock it.
            </div>
          </Form>
        </Modal>
        </>
      )}
    </div>
  );
};

export default Innskytingsliste;
